// Safari module loading diagnostics.
// Record failed module resources without changing browser behavior.
const sentryModuleLoadFailures = [];

function describeSentryModuleUrl(value) {
    if (typeof value !== 'string' || !value) {
        return null;
    }

    try {
        const url = new URL(value, location.href);

        return {
            origin: url.origin,
            pathname: url.pathname,
            sameOrigin: url.origin === location.origin,
        };
    } catch {
        return null;
    }
}

window.addEventListener('error', event => {
    try {
        const target = event.target;

        if (!(target instanceof Element)) return;

        let resourceType = null;
        let resourceUrl = null;

        if (
            target.tagName === 'SCRIPT' &&
            target.getAttribute('type')?.trim().toLowerCase() === 'module'
        ) {
            resourceType = 'module-script';
            resourceUrl = target.src;
        } else if (
            target.tagName === 'LINK' &&
            target.relList?.contains('modulepreload')
        ) {
            resourceType = 'modulepreload';
            resourceUrl = target.href;
        }

        if (!resourceType) return;

        sentryModuleLoadFailures.push({
            timestamp: Date.now(),
            resourceType,
            url: describeSentryModuleUrl(resourceUrl),
            online: navigator.onLine,
            visibilityState: document.visibilityState,
        });

        if (sentryModuleLoadFailures.length > 15) {
            sentryModuleLoadFailures.shift();
        }
    } catch {
        // Diagnostics must not affect normal page execution.
    }
}, true);

window.GambodySentryDiagnostics = async (event, hint, error, exception, message, errorValue, userAgent, navigationState) => {
    // Shared helpers
    const getBreadcrumbs = () => Array.isArray(event?.breadcrumbs) ? event.breadcrumbs : [];

    const getBreadcrumbText = breadcrumb => {
        const args = Array.isArray(breadcrumb?.data?.arguments)
            ? breadcrumb.data.arguments
                .filter(value => typeof value === 'string')
                .join('\n')
            : '';

        return [
            breadcrumb?.message,
            breadcrumb?.category,
            breadcrumb?.data?.message,
            args,
        ]
            .filter(value => typeof value === 'string')
            .join('\n');
    };

    const getLastBreadcrumbs = (limit = 30) => getBreadcrumbs().slice(-limit);

    const getUiBreadcrumbs = (limit = 20, includeConsole = false) =>
        getBreadcrumbs()
            .filter(b =>
                b?.category === 'ui.click' ||
                b?.category === 'ui.input' ||
                b?.category === 'navigation' ||
                (includeConsole && b?.category === 'console')
            )
            .slice(-limit)
            .map(b => ({
                category: b.category,
                level: b.level,
                message: b.message,
                data: b.data,
            }));

    const getNetworkBreadcrumbs = (limit = 20) =>
        getBreadcrumbs()
            .filter(b =>
                b?.category === 'fetch' ||
                b?.category === 'xhr'
            )
            .slice(-limit)
            .map(b => ({
                category: b.category,
                level: b.level,
                type: b.type,
                message: b.message,
                method: b?.data?.method,
                statusCode: b?.data?.status_code,
                url: b?.data?.url,
            }));

    const addTags = tags => {
        event.tags = {
            ...(event.tags || {}),
            ...tags,
        };
    };

    const addExtra = extra => {
        event.extra = {
            ...(event.extra || {}),
            ...extra,
        };
    };

    const getPageContext = () => ({
        url: location.href,
        pathname: location.pathname,
        referrer: document.referrer,
        title: document.title,
        visibilityState: document.visibilityState,
        readyState: document.readyState,
    });

    const getBrowserContext = (includeLanguages = false) => ({
        userAgent: navigator.userAgent,
        language: navigator.language,
        online: navigator.onLine,
        cookieEnabled: navigator.cookieEnabled,
        platform: navigator.platform,
        ...(includeLanguages ? { languages: navigator.languages } : {}),
    });

    const getNavigationContext = () => {
        const entry = performance.getEntriesByType('navigation')?.[0];

        if (!entry) {
            return null;
        }

        return {
            type: entry.type,
            redirectCount: entry.redirectCount,
            duration: Math.round(entry.duration),
            startTime: entry.startTime,
            requestStart: entry.requestStart,
            responseStart: entry.responseStart,
            responseEnd: entry.responseEnd,
            transferSize: entry.transferSize,
            encodedBodySize: entry.encodedBodySize,
            decodedBodySize: entry.decodedBodySize,
            nextHopProtocol: entry.nextHopProtocol,
        };
    };

    const getResourceContext = (predicate, limit = 30) =>
        performance.getEntriesByType('resource')
            .filter(predicate)
            .slice(-limit)
            .map(resource => ({
                name: resource.name,
                initiatorType: resource.initiatorType,
                duration: Math.round(resource.duration),
                transferSize: resource.transferSize,
                encodedBodySize: resource.encodedBodySize,
                decodedBodySize: resource.decodedBodySize,
                nextHopProtocol: resource.nextHopProtocol,
            }));

    // Detailed Resource Timing diagnostics for failed HTML component requests
    const getComponentFetchNetworkDiagnostics = (failedUrl) => {
        if (typeof failedUrl !== 'string' || !failedUrl) {
            return null;
        }

        try {
            const targetUrl = new URL(failedUrl, location.href);

            if (targetUrl.origin !== location.origin) {
                return null;
            }

            const now = performance.now();
            const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

            const getTiming = (entry) => {
                const url = new URL(entry.name, location.href);

                return {
                    path: url.pathname,
                    startTime: entry.startTime,
                    duration: entry.duration,

                    fetchStart: entry.fetchStart,
                    redirectStart: entry.redirectStart,
                    redirectEnd: entry.redirectEnd,

                    domainLookupStart: entry.domainLookupStart,
                    domainLookupEnd: entry.domainLookupEnd,

                    connectStart: entry.connectStart,
                    connectEnd: entry.connectEnd,
                    secureConnectionStart: entry.secureConnectionStart,

                    requestStart: entry.requestStart,
                    responseStart: entry.responseStart,
                    responseEnd: entry.responseEnd,
                    responseStatus: entry.responseStatus ?? null,

                    transferSize: entry.transferSize,
                    encodedBodySize: entry.encodedBodySize,
                    decodedBodySize: entry.decodedBodySize,

                    nextHopProtocol: entry.nextHopProtocol,
                    workerStart: entry.workerStart,
                    deliveryType: entry.deliveryType ?? null,
                };
            };

            const resources = performance.getEntriesByType('resource')
                .filter(entry => {
                    if (entry.initiatorType !== 'fetch') {
                        return false;
                    }

                    const url = new URL(entry.name, location.href);

                    return (
                        url.origin === location.origin &&
                        /^\/components\/.*\.html$/i.test(url.pathname)
                    );
                });

            const failedResource = [...resources]
                .reverse()
                .find(entry => entry.name === targetUrl.href);

            const recentComponents = resources
                .filter(entry => entry.startTime >= now - 5000)
                .slice(-15);

            return {
                failedResourceFound: Boolean(failedResource),
                failedResource: failedResource ? getTiming(failedResource) : null,
                recentComponentResources: recentComponents.map(getTiming),
                recentComponentResourceCount: recentComponents.length,
                serviceWorkerControlled: 'serviceWorker' in navigator
                    ? Boolean(navigator.serviceWorker.controller)
                    : null,
                connection: connection
                    ? {
                        effectiveType: connection.effectiveType ?? null,
                        downlink: connection.downlink ?? null,
                        rtt: connection.rtt ?? null,
                        saveData: connection.saveData ?? null,
                    }
                    : null,
                navigation: {
                    reason: navigationState.reason,
                    elapsedMs: navigationState.timestamp ? Date.now() - navigationState.timestamp : null,
                },
            };
        } catch {
            return null;
        }
    };

    const getScriptSources = () =>
        Array.from(document.scripts)
            .map(script => script.src)
            .filter(Boolean);

    const getExceptionDetails = originalException => originalException
        ? {
            type: originalException?.constructor?.name,
            name: originalException?.name,
            message: originalException?.message,
            stack: originalException?.stack,
        }
        : null;

    const getFrameContext = frame => frame
        ? {
            filename: frame.filename,
            function: frame.function,
            module: frame.module,
            package: frame.package,
            lineno: frame.lineno,
            colno: frame.colno,
            inApp: frame.in_app,
            contextLine: frame.context_line,
            preContext: frame.pre_context,
            postContext: frame.post_context,
        }
        : null;

    const getSafeStorageKeys = storage => {
        try {
            return Object.keys(storage);
        } catch {
            return null;
        }
    };

    // Compatible alternative to String.prototype.matchAll().
    const getRegexMatches = (text, regex) => {
        const matches = [];

        if (typeof text !== 'string') {
            return matches;
        }

        try {
            let match;

            while ((match = regex.exec(text)) !== null) {
                matches.push(match);

                if (!regex.global) {
                    break;
                }

                // Prevent infinite loops for empty matches.
                if (match[0] === '') {
                    regex.lastIndex++;
                }
            }
        } finally {
            regex.lastIndex = 0;
        }

        return matches;
    };

    const frames = exception?.stacktrace?.frames || [];
    const topFrame = frames.length ? frames[frames.length - 1] : null;

    // ============================================================
    // PERMANENT DIAGNOSTICS
    // ============================================================

    // Alpine x-for / items diagnostics
    {
        const breadcrumbText = getBreadcrumbs().map(getBreadcrumbText).join('\n');

        const exceptionStack = frames
            .map(frame => [
                frame?.filename,
                frame?.function,
                frame?.lineno,
            ].filter(Boolean).join(':'))
            .join('\n');

        const fullErrorText = [
            errorValue,
            message,
            exception?.type,
            exceptionStack,
        ].join('\n');

        const isAfterError = /Cannot read properties of undefined \(reading ['"]after['"]\)/i.test(fullErrorText)
            || /undefined is not an object \(evaluating ['"][^'"]*\.after['"]\)/i.test(fullErrorText)
            || /can't access property ['"]after['"],?\s*[^,\n]*\bis undefined/i.test(fullErrorText);
        const isAlpineInternalError = /_x_currentIfEl|_x_refreshXForScope/i.test(fullErrorText);
        const isLengthError = /Cannot read properties of undefined \(reading ['"]length['"]\)/i.test(fullErrorText)
            || /undefined is not an object \(evaluating ['"][^'"]*\.length['"]\)/i.test(fullErrorText)
            || /can't access property ['"]length['"],.*\bis undefined/i.test(fullErrorText);
        const relatedAlpineXForBreadcrumbs =
            /Duplicate key on x-for|x-for\s+["']?:key["']?\s+is undefined or invalid|\bhasCustomLists\b/i
                .test(breadcrumbText);

        const shouldCaptureAlpineXFor = isAfterError
            || isAlpineInternalError
            || (isLengthError && relatedAlpineXForBreadcrumbs);

        if (shouldCaptureAlpineXFor) {
            const alpine = typeof window !== 'undefined' ? window.Alpine : undefined;

            const safeGetAttribute = (element, name) => {
                try {
                    return element?.getAttribute?.(name) ?? null;
                } catch {
                    return null;
                }
            };

            const safeKeys = (value, limit = 60) => {
                try {
                    if (
                        value &&
                        (
                            typeof value === 'object' ||
                            typeof value === 'function'
                        )
                    ) {
                        return Object.keys(value).slice(0, limit);
                    }
                } catch {
                    // Object inspection can fail during DOM updates.
                }

                return [];
            };

            const safeScalar = value => {
                try {
                    if (
                        value === null ||
                        value === undefined ||
                        ['string', 'number', 'boolean'].includes(typeof value)
                    ) {
                        return value;
                    }

                    if (typeof value === 'bigint') {
                        return String(value);
                    }

                    if (Array.isArray(value)) {
                        return `[Array(${value.length})]`;
                    }

                    if (typeof value === 'object') {
                        return '[object]';
                    }

                    return String(value);
                } catch {
                    return '[unavailable]';
                }
            };

            const describeNode = node => {
                if (!node?.tagName) {
                    return null;
                }

                return {
                    tag: String(node.tagName).toLowerCase(),
                    id: safeGetAttribute(node, 'id'),
                    className: safeGetAttribute(node, 'class'),
                    xData: safeGetAttribute(node, 'x-data'),
                    xFor: safeGetAttribute(node, 'x-for'),
                    xIf: safeGetAttribute(node, 'x-if'),
                    connected: Boolean(node.isConnected),
                };
            };

            const getCollectionPath = expression => {
                if (typeof expression !== 'string') {
                    return null;
                }

                return expression.match(/\b(?:in|of)\s+([$\w.]+)/)?.[1] || null;
            };

            const getPathValue = (scope, path) => {
                if (!scope || !path) {
                    return undefined;
                }

                try {
                    return path.split('.').reduce((value, key) => {
                        return value == null ? undefined : value[key];
                    }, scope);
                } catch {
                    return undefined;
                }
            };

            const getScopes = element => {
                const scopes = [];
                const seen = new Set();

                const addScope = scope => {
                    if (
                        scope &&
                        ['object', 'function'].includes(typeof scope) &&
                        !seen.has(scope)
                    ) {
                        seen.add(scope);
                        scopes.push(scope);
                    }
                };

                let current = element;
                let depth = 0;

                while (current && depth < 12) {
                    try {
                        if (Array.isArray(current._x_dataStack)) {
                            current._x_dataStack.forEach(addScope);
                        }
                    } catch {
                        // Alpine's internal scope may be unavailable.
                    }

                    try {
                        if (typeof alpine?.$data === 'function') {
                            addScope(alpine.$data(current));
                        }
                    } catch {
                        // $data may fail during initialization.
                    }

                    current = current.parentElement;
                    depth += 1;
                }

                return scopes;
            };

            const summarizeCollectionItem = item => {
                if (item == null || typeof item !== 'object') {
                    return safeScalar(item);
                }

                const result = {};

                for (const key of [
                    'id',
                    'bundleId',
                    'bundle_id',
                    'key',
                    'slug',
                    'name',
                ]) {
                    try {
                        if (key in item) {
                            result[key] = safeScalar(item[key]);
                        }
                    } catch {
                        result[key] = '[unavailable]';
                    }
                }

                if (Object.keys(result).length === 0) {
                    result.availableKeys = safeKeys(item, 20);
                }

                return result;
            };

            const describeCollection = value => {
                try {
                    if (Array.isArray(value)) {
                        return {
                            type: 'array',
                            length: value.length,
                            sample: value
                                .slice(0, 10)
                                .map(summarizeCollectionItem),
                        };
                    }

                    if (value && typeof value === 'object') {
                        return {
                            type: 'object',
                            keys: safeKeys(value, 30),
                        };
                    }

                    return {
                        type: typeof value,
                        value: safeScalar(value),
                    };
                } catch {
                    return { type: 'unavailable' };
                }
            };

            const getTemplateDiagnostics = template => {
                const xForExpression =
                    safeGetAttribute(template, 'x-for');

                const keyExpression =
                    safeGetAttribute(template, ':key') ||
                    safeGetAttribute(template, 'x-bind:key') ||
                    safeGetAttribute(template, 'x-bind\\:key');

                const collectionPath =
                    getCollectionPath(xForExpression);

                const scopes = getScopes(template?.parentElement || template);

                let collectionFound = false;
                let collectionSummary = null;
                let matchingScopeKeys = [];

                if (collectionPath) {
                    for (const scope of scopes) {
                        const value = getPathValue(scope, collectionPath);

                        if (value !== undefined) {
                            collectionFound = true;
                            collectionSummary = describeCollection(value);
                            matchingScopeKeys = safeKeys(scope);
                            break;
                        }
                    }
                }

                let previousKeys = null;
                let lookupKeys = null;

                try {
                    if (Array.isArray(template?._x_prevKeys)) {
                        previousKeys = template._x_prevKeys
                            .slice(0, 30)
                            .map(safeScalar);
                    }
                } catch {
                    previousKeys = '[unavailable]';
                }

                try {
                    const lookup = template?._x_lookup;

                    if (lookup && typeof lookup.keys === 'function') {
                        lookupKeys = Array.from(lookup.keys())
                            .slice(0, 30)
                            .map(safeScalar);
                    } else if (lookup) {
                        lookupKeys = safeKeys(lookup, 30);
                    }
                } catch {
                    lookupKeys = '[unavailable]';
                }

                const scopeKeys = new Set();

                for (const scope of scopes) {
                    safeKeys(scope).forEach(key => scopeKeys.add(key));
                }

                let parent = null;
                let alpineRoot = null;

                try {
                    parent = describeNode(template?.parentElement);
                    alpineRoot = describeNode(template?.closest?.('[x-data]'));
                } catch {
                    // The DOM may change during inspection.
                }

                return {
                    tag: String(template?.tagName || '').toLowerCase(),
                    connected: Boolean(template?.isConnected),
                    xFor: xForExpression,
                    keyExpression,
                    collectionPath,
                    collectionFound,
                    collection: collectionSummary,
                    collectionScopeKeys: matchingScopeKeys,
                    scopeKeys: Array.from(scopeKeys).slice(0, 80),
                    previousKeys,
                    lookupKeys,
                    alpineInitialized: Boolean(template?._x_marker),
                    hasXForLookup: Boolean(template?._x_lookup),
                    hasPreviousKeys: Array.isArray(template?._x_prevKeys),
                    parent,
                    alpineRoot,
                    outerHTML: typeof template?.outerHTML === 'string'
                        ? template.outerHTML.slice(0, 1200)
                        : null,
                };
            };

            const exceptionElement =
                error?.el &&
                typeof error.el.closest === 'function'
                    ? error.el
                    : null;

            let originalXForTemplate = null;

            try {
                if (exceptionElement) {
                    originalXForTemplate =
                        exceptionElement.matches('[x-for]')
                            ? exceptionElement
                            : exceptionElement.closest('[x-for]');
                }
            } catch {
                originalXForTemplate = null;
            }

            const allXForTemplates =
                typeof document !== 'undefined'
                    ? Array.from(document.querySelectorAll('[x-for]'))
                    : [];

            const relevantBreadcrumbs = getBreadcrumbs()
                .map((breadcrumb, index) => ({
                    index,
                    breadcrumb,
                    text: getBreadcrumbText(breadcrumb),
                }))
                .filter(item =>
                    /Duplicate key on x-for|x-for\s+["']?:key["']?\s+is undefined or invalid|\bhasCustomLists\b|\bnavList\b|_x_currentIfEl|_x_refreshXForScope/i
                        .test(item.text)
                )
                .slice(-20)
                .map(({ index, breadcrumb, text }) => ({
                    index,
                    category: breadcrumb?.category,
                    level: breadcrumb?.level,
                    message: breadcrumb?.message,
                    timestamp: breadcrumb?.timestamp,
                    text: text.slice(0, 1000),
                }));

            const breadcrumbContext = relevantBreadcrumbs
                .map(item => item.text)
                .join('\n');

            const expressionHints = new Set();

            for (const source of [breadcrumbContext, fullErrorText]) {
                const matches = getRegexMatches(
                    source,
                    /\b(?:navList|hasCustomLists)\b/gi
                );

                matches.forEach(match => {
                    expressionHints.add(match[0].toLowerCase());
                });
            }

            const rankedTemplates = allXForTemplates
                .map((template, index) => {
                    const expression =
                        safeGetAttribute(template, 'x-for') || '';

                    const lowerExpression = expression.toLowerCase();

                    let score = 0;

                    for (const hint of expressionHints) {
                        if (lowerExpression.includes(hint)) {
                            score += 10;
                        }
                    }

                    if (/navList|hasCustomLists/i.test(expression)) {
                        score += 5;
                    }

                    if (template === originalXForTemplate) {
                        score += 1000;
                    }

                    return { template, index, score };
                })
                .sort((a, b) => b.score - a.score || a.index - b.index);

            const selectedTemplates = [];

            if (originalXForTemplate) {
                selectedTemplates.push(originalXForTemplate);
            }

            for (const item of rankedTemplates) {
                if (!selectedTemplates.includes(item.template)) {
                    selectedTemplates.push(item.template);
                }

                if (selectedTemplates.length >= 20) {
                    break;
                }
            }

            let pageHref = null;
            let pageReferrer = null;
            let currentUserAgent = null;

            try {
                pageHref = window.location.href;
                pageReferrer = document.referrer || null;
                currentUserAgent = navigator.userAgent;
            } catch {
                // Browser context may be unavailable.
            }

            addExtra({
                alpineXForDiagnostics: {
                    trigger: {
                        errorText: fullErrorText.slice(0, 2000),
                        isAfterError,
                        isAlpineInternalError,
                        isLengthError,
                        relatedAlpineXForBreadcrumbs,
                    },
                    page: {
                        href: pageHref,
                        referrer: pageReferrer,
                        userAgent: currentUserAgent,
                    },
                    breadcrumbContext: relevantBreadcrumbs,
                    templatesFound: allXForTemplates.length,
                    originalExceptionElement: describeNode(exceptionElement),
                    originalXForTemplateFound: Boolean(originalXForTemplate),
                    expressionHints: Array.from(expressionHints),
                    templates: selectedTemplates.map(getTemplateDiagnostics),
                },
            });
        }
    }

    // Component/template loading errors
    const isComponentTemplateUrl = (() => {
        if (typeof error?.fetchUrl !== 'string') {
            return false;
        }

        try {
            const url = new URL(error.fetchUrl, location.href);

            return (
                url.origin === location.origin &&
                /\/components\/.*\.html$/i.test(url.pathname)
            );
        } catch {
            return false;
        }
    })();

    if (isComponentTemplateUrl || message.includes('Failed to fetch template')) {
        const failedTemplateMatch = message.match(
            /Failed to fetch template ["']([^"']+)["']/
        );

        const failedTemplateUrl =
            error?.fetchUrl ||
            failedTemplateMatch?.[1] ||
            null;

        // Collect fetch breadcrumbs for all HTML components.
        const componentFetchBreadcrumbs = getBreadcrumbs()
            .filter(b =>
                (b?.category === 'fetch' || b?.category === 'xhr') &&
                /\/components\/.*\.html(?:[?#]|$)/i.test(
                    String(b?.data?.url || '')
                )
            )
            .slice(-30)
            .map(b => ({
                category: b.category,
                type: b.type,
                level: b.level,
                message: b.message,
                timestamp: b.timestamp,
                method: b?.data?.method,
                statusCode: b?.data?.status_code ?? null,
                url: b?.data?.url,
            }));

        const templateFetchBreadcrumbs = failedTemplateUrl
            ? getBreadcrumbs()
                .filter(b =>
                    (b?.category === 'fetch' || b?.category === 'xhr') &&
                    String(b?.data?.url || '').includes(failedTemplateUrl)
                )
                .slice(-10)
                .map(b => ({
                    category: b.category,
                    type: b.type,
                    level: b.level,
                    message: b.message,
                    timestamp: b.timestamp,
                    method: b?.data?.method,
                    statusCode: b?.data?.status_code ?? null,
                    url: b?.data?.url,
                }))
            : [];

        const components = Array.from(
            document.querySelectorAll('a-component')
        ).map(el => ({
            template: el.getAttribute('template'),
            xCloak: el.hasAttribute('x-cloak'),
            connected: el.isConnected,
        }));

        const failedTemplateResource = failedTemplateUrl
            ? getResourceContext(
                entry => entry.name.includes(failedTemplateUrl),
                5
            )
            : [];

        // Collect detailed network timing for failed component requests.
        const getComponentFetchNetworkDiagnostics = (fetchUrl) => {
            if (typeof fetchUrl !== 'string' || !fetchUrl) {
                return null;
            }

            try {
                const targetUrl = new URL(fetchUrl, location.href);

                if (targetUrl.origin !== location.origin) {
                    return null;
                }

                const now = performance.now();

                const connection =
                    navigator.connection ||
                    navigator.mozConnection ||
                    navigator.webkitConnection;

                const getTiming = entry => {
                    const url = new URL(entry.name, location.href);

                    return {
                        path: url.pathname,
                        startTime: entry.startTime,
                        duration: entry.duration,

                        fetchStart: entry.fetchStart,
                        redirectStart: entry.redirectStart,
                        redirectEnd: entry.redirectEnd,

                        domainLookupStart: entry.domainLookupStart,
                        domainLookupEnd: entry.domainLookupEnd,

                        connectStart: entry.connectStart,
                        connectEnd: entry.connectEnd,
                        secureConnectionStart: entry.secureConnectionStart,

                        requestStart: entry.requestStart,
                        responseStart: entry.responseStart,
                        responseEnd: entry.responseEnd,
                        responseStatus: entry.responseStatus ?? null,

                        transferSize: entry.transferSize,
                        encodedBodySize: entry.encodedBodySize,
                        decodedBodySize: entry.decodedBodySize,

                        nextHopProtocol: entry.nextHopProtocol,
                        workerStart: entry.workerStart,
                        deliveryType: entry.deliveryType ?? null,
                    };
                };

                const resources = performance
                    .getEntriesByType('resource')
                    .filter(entry => {
                        if (entry.initiatorType !== 'fetch') {
                            return false;
                        }

                        const url = new URL(entry.name, location.href);

                        return (
                            url.origin === location.origin &&
                            /^\/components\/.*\.html$/i.test(url.pathname)
                        );
                    });

                // Find the latest timing entry for the exact failed URL.
                const failedResource = [...resources]
                    .reverse()
                    .find(entry => entry.name === targetUrl.href);

                // Include recently started component requests for comparison.
                const recentComponents = resources
                    .filter(entry => entry.startTime >= now - 5000)
                    .slice(-15);

                const navigationElapsedMs = navigationState.timestamp
                    ? Date.now() - navigationState.timestamp
                    : null;

                return {
                    failedResourceFound: Boolean(failedResource),

                    failedResource: failedResource
                        ? getTiming(failedResource)
                        : null,

                    recentComponentResources: recentComponents.map(getTiming),

                    recentComponentResourceCount: recentComponents.length,

                    serviceWorkerControlled:
                        'serviceWorker' in navigator
                            ? Boolean(navigator.serviceWorker.controller)
                            : null,

                    connection: connection
                        ? {
                            effectiveType: connection.effectiveType ?? null,
                            downlink: connection.downlink ?? null,
                            rtt: connection.rtt ?? null,
                            saveData: connection.saveData ?? null,
                        }
                        : null,

                    navigation: {
                        reason: navigationElapsedMs !== null &&
                        navigationElapsedMs >= 0 &&
                        navigationElapsedMs <= 5000
                            ? navigationState.reason
                            : null,

                        elapsedMs: navigationElapsedMs !== null &&
                        navigationElapsedMs >= 0 &&
                        navigationElapsedMs <= 5000
                            ? navigationElapsedMs
                            : null,
                    },
                };
            } catch {
                return null;
            }
        };

        const componentFetchNetwork = failedTemplateUrl
            ? getComponentFetchNetworkDiagnostics(failedTemplateUrl)
            : null;

        const isTemplate404 =
            templateFetchBreadcrumbs.some(b => b.statusCode === 404) ||
            /\(404\)/i.test(message);

        addTags({
            feature: 'component-loader',
            component_template_error: 'true',
            ...(isTemplate404 ? { component_template_404: 'true' } : {}),
        });

        addExtra({
            componentLoader: {
                errorMessage: message,
                fetchUrl: failedTemplateUrl,
                isTemplate404,
            },
            page: getPageContext(),
            browser: getBrowserContext(),
            components,
            componentFetchBreadcrumbs,
            templateFetchBreadcrumbs,
            failedTemplateResource,
            componentFetchNetwork,
            lastBreadcrumbs: getLastBreadcrumbs(30),
        });
    }

    // Maximum call stack size exceeded
    if (errorValue.includes('Maximum call stack size exceeded')) {
        const repeatedFrames = frames.reduce((acc, frame) => {
            const key =
                `${frame.function || '?'}:${frame.lineno ?? '?'}:${frame.colno ?? '?'}`;

            acc[key] = (acc[key] || 0) + 1;
            return acc;
        }, {});

        const repeatedFramePairs = frames.reduce((acc, frame, index) => {
            if (index === 0) {
                return acc;
            }

            const previous = frames[index - 1];

            const previousKey =
                `${previous.function || '?'}:${previous.lineno ?? '?'}:${previous.colno ?? '?'}`;

            const currentKey =
                `${frame.function || '?'}:${frame.lineno ?? '?'}:${frame.colno ?? '?'}`;

            const key = `${previousKey} -> ${currentKey}`;

            acc[key] = (acc[key] || 0) + 1;
            return acc;
        }, {});

        // Map stack coordinates to the original HTML response.
        const originalHtmlScriptMapping = await (async () => {
            let controller = null;
            let timeoutId = null;

            try {
                const pageFrames = [];
                const seenPositions = new Set();

                for (const frame of frames) {
                    if (
                        !frame?.filename ||
                        !Number.isInteger(frame.lineno) ||
                        !Number.isInteger(frame.colno)
                    ) {
                        continue;
                    }

                    let frameUrl;

                    try {
                        frameUrl = new URL(frame.filename, location.href);
                    } catch {
                        continue;
                    }

                    if (
                        frameUrl.origin !== location.origin ||
                        frameUrl.pathname !== location.pathname
                    ) {
                        continue;
                    }

                    const key = `${frame.lineno}:${frame.colno}`;

                    if (!seenPositions.has(key)) {
                        seenPositions.add(key);

                        pageFrames.push({
                            function: frame.function,
                            line: frame.lineno,
                            column: frame.colno,
                        });
                    }
                }

                if (!pageFrames.length) {
                    return {
                        attempted: false,
                        reason: 'No stack frames pointing to the current document',
                    };
                }

                controller = typeof AbortController === 'function'
                    ? new AbortController()
                    : null;

                timeoutId = controller
                    ? setTimeout(() => controller.abort(), 3000)
                    : null;

                const response = await fetch(location.href, {
                    credentials: 'same-origin',
                    cache: 'force-cache',
                    ...(controller ? { signal: controller.signal } : {}),
                });

                if (!response.ok) {
                    return {
                        attempted: true,
                        responseStatus: response.status,
                        responseUrl: response.url,
                    };
                }

                const html = await response.text();
                const lines = html.split('\n');
                const lineOffsets = [];

                let currentOffset = 0;

                for (const line of lines) {
                    lineOffsets.push(currentOffset);
                    currentOffset += line.length + 1;
                }

                // Find script ranges in the original HTML.
                const scriptRanges = [];
                const scriptRegex = /<script\b[^>]*>[\s\S]*?<\/script\s*>/gi;
                const lowerHtml = html.toLowerCase();

                let match;

                while ((match = scriptRegex.exec(html)) !== null) {
                    const openingTagEnd = html.indexOf('>', match.index) + 1;

                    const closingTagStart = lowerHtml.indexOf(
                        '</script',
                        openingTagEnd
                    );

                    if (openingTagEnd <= 0 || closingTagStart < 0) {
                        continue;
                    }

                    scriptRanges.push({
                        index: scriptRanges.length,
                        start: match.index,
                        bodyStart: openingTagEnd,
                        bodyEnd: closingTagStart,
                    });
                }

                // Parse HTML to identify the script's attributes and ancestors.
                const parsedDocument = new DOMParser().parseFromString(
                    html,
                    'text/html'
                );

                const parsedScripts = [];

                const collectScripts = node => {
                    if (
                        node.nodeType === 1 &&
                        node.tagName === 'SCRIPT'
                    ) {
                        parsedScripts.push(node);
                    }

                    const children =
                        node.nodeType === 1 &&
                        node.tagName === 'TEMPLATE' &&
                        node.content
                            ? node.content.childNodes
                            : node.childNodes;

                    Array.from(children || []).forEach(collectScripts);
                };

                collectScripts(parsedDocument);

                const describeNode = node => {
                    if (!node || node.nodeType !== 1) {
                        return null;
                    }

                    const attributes = {};

                    for (const name of [
                        'id',
                        'class',
                        'href',
                        'data-id',
                        'data-model-id',
                        'data-item-id',
                        'data-slug',
                        'data-testid',
                    ]) {
                        const value = node.getAttribute(name);

                        if (value) {
                            attributes[name] = value.slice(0, 180);
                        }
                    }

                    return {
                        tag: node.tagName.toLowerCase(),
                        attributes,
                    };
                };

                const mapFrame = frame => {
                    const lineIndex = frame.line - 1;
                    const sourceLine = lines[lineIndex] || '';
                    const columnIndex = frame.column - 1;

                    const validPosition =
                        lineIndex >= 0 &&
                        lineIndex < lines.length &&
                        columnIndex >= 0 &&
                        columnIndex <= sourceLine.length;

                    const sourceOffset = validPosition
                        ? lineOffsets[lineIndex] + columnIndex
                        : null;

                    const scriptRange = sourceOffset === null
                        ? null
                        : scriptRanges.find(range =>
                        sourceOffset >= range.bodyStart &&
                        sourceOffset < range.bodyEnd
                    ) || null;

                    const scriptNode = scriptRange
                        ? parsedScripts[scriptRange.index] || null
                        : null;

                    const ownerChain = [];
                    let parent = scriptNode;

                    for (let depth = 0; parent && depth < 10; depth++) {
                        ownerChain.push(describeNode(parent));
                        parent = parent.parentElement;
                    }

                    return {
                        function: frame.function,
                        line: frame.line,
                        column: frame.column,
                        sourceLineLength: sourceLine.length,

                        sourceExcerpt: sourceLine.slice(
                            Math.max(0, columnIndex - 120),
                            Math.min(sourceLine.length, columnIndex + 180)
                        ),

                        sourceOffsetFound: sourceOffset !== null,
                        insideScriptBody: Boolean(scriptRange),
                        scriptIndex: scriptRange?.index ?? null,

                        script: scriptNode
                            ? {
                                id: scriptNode.id || null,
                                type: scriptNode.getAttribute('type'),
                                src: scriptNode.getAttribute('src'),
                                contentLength:
                                    scriptRange.bodyEnd - scriptRange.bodyStart,
                                ownerChain,
                            }
                            : null,
                    };
                };

                return {
                    attempted: true,
                    responseUrl: response.url,
                    responseStatus: response.status,
                    htmlLength: html.length,
                    sourceScriptCount: scriptRanges.length,
                    parsedScriptCount: parsedScripts.length,
                    locations: pageFrames.slice(0, 12).map(mapFrame),
                };
            } catch (mappingError) {
                return {
                    attempted: true,
                    error: mappingError?.message || String(mappingError),
                };
            } finally {
                if (timeoutId !== null) {
                    clearTimeout(timeoutId);
                }
            }
        })();

        // Context around stack lines in the current DOM serialization.
        const documentLines = document.documentElement.outerHTML.split('\n');

        const documentStackContext = [...new Set(
            frames
                .map(frame => frame?.lineno)
                .filter(Number.isInteger)
        )].map(line => {
            const start = Math.max(1, line - 3);
            const end = Math.min(documentLines.length, line + 3);

            return {
                line,
                start,
                end,
                lines: documentLines
                    .slice(start - 1, end)
                    .map((content, index) => ({
                        line: start + index,
                        content: content.slice(0, 3000),
                    })),
            };
        });

        // Inventory scripts currently present in the DOM.
        const scriptInventory = Array.from(document.scripts)
            .slice(0, 100)
            .map((script, index) => {
                const source = script.src
                    ? ''
                    : (script.textContent || '');

                let src = null;

                if (script.src) {
                    try {
                        const parsedUrl = new URL(script.src, location.href);
                        src = `${parsedUrl.origin}${parsedUrl.pathname}`;
                    } catch {
                        src = script.src.split(/[?#]/)[0];
                    }
                }

                return {
                    index,
                    src,
                    id: script.id || null,
                    type: script.type || 'classic',
                    async: script.async,
                    defer: script.defer,
                    inlineLength: source.length,
                    containsTargetFunction:
                        source.includes('findTopmostVisibleElement'),
                    containsPositionedElements:
                        /\bpositionedElements\b/i.test(source),
                };
            });

        // Search inline scripts by content instead of treating document
        // line numbers as line numbers inside every individual script.
        const matchingInlineScripts = Array.from(
            document.querySelectorAll('script:not([src])')
        )
            .map((script, index) => {
                const source = script.textContent || '';

                const terms = [
                    'findTopmostVisibleElement',
                    'positionedElements',
                ];

                const matches = terms
                    .map(term => {
                        const matchIndex = source.indexOf(term);

                        if (matchIndex < 0) {
                            return null;
                        }

                        return {
                            term,
                            sourceExcerpt: source.slice(
                                Math.max(0, matchIndex - 250),
                                Math.min(source.length, matchIndex + 750)
                            ),
                        };
                    })
                    .filter(Boolean);

                if (!matches.length) {
                    return null;
                }

                return {
                    index,
                    id: script.id || null,
                    type: script.type || 'classic',
                    sourceLength: source.length,
                    matches,
                };
            })
            .filter(Boolean)
            .slice(0, 20);

        // JavaScript resources loaded before the exception.
        const jsResources = performance
            .getEntriesByType('resource')
            .filter(resource =>
                resource.initiatorType === 'script' ||
                /\.m?js(?:[?#]|$)/i.test(resource.name)
            )
            .slice(-60)
            .map(resource => {
                let url = resource.name;

                try {
                    const parsedUrl = new URL(resource.name, location.href);
                    url = `${parsedUrl.origin}${parsedUrl.pathname}`;
                } catch {
                    url = resource.name.split(/[?#]/)[0];
                }

                return {
                    url,
                    initiatorType: resource.initiatorType,
                    duration: Math.round(resource.duration),
                    transferSize: resource.transferSize || null,
                };
            });

        const activeElement = document.activeElement;

        const activeElementContext = activeElement
            ? {
                tag: activeElement.tagName,
                id: activeElement.id || null,
                className: activeElement.className || null,
                parentChain: (() => {
                    const chain = [];
                    let current = activeElement;

                    for (let i = 0; current && i < 6; i++) {
                        chain.push({
                            tag: current.tagName,
                            id: current.id || null,
                            className: current.className || null,
                        });

                        current = current.parentElement;
                    }

                    return chain;
                })(),
            }
            : null;

        addTags({
            stack_overflow: 'true',
            ...(topFrame?.function
                ? { stack_overflow_top_function: topFrame.function }
                : {}),
            ...(topFrame?.lineno
                ? { stack_overflow_top_line: String(topFrame.lineno) }
                : {}),
        });

        addExtra({
            url: location.href,
            pathname: location.pathname,
            page: getPageContext(),

            browser: {
                ...getBrowserContext(),
                memory: navigator.deviceMemory,
                cores: navigator.hardwareConcurrency,
                viewport: `${window.innerWidth}x${window.innerHeight}`,
                devicePixelRatio: window.devicePixelRatio,
            },

            exception: {
                type: exception?.type,
                value: exception?.value,
                mechanism: exception?.mechanism,
            },

            topFrame: getFrameContext(topFrame),
            stackFrames: frames.map(getFrameContext),

            recursion: {
                depth: frames.length,
                repeatedFrames,
                repeatedFramePairs,
            },

            documentStackContext,
            originalHtmlScriptMapping,

            // New source diagnostics.
            scriptInventory,
            matchingInlineScripts,
            jsResources,

            activeElement: activeElementContext,
            originalStack: error?.stack,

            recentUiBreadcrumbs: getUiBreadcrumbs(20, true),
            recentNetworkBreadcrumbs: getNetworkBreadcrumbs(20),
            lastBreadcrumbs: getLastBreadcrumbs(30),
        });
    }

    // First-party Axios ERR_NETWORK
    if (error?.isAxiosError && error?.code === 'ERR_NETWORK') {
        const requestUrl = error?.config?.url || '';
        const baseURL = error?.config?.baseURL || '';

        let isFirstParty = false;

        try {
            const absoluteUrl = new URL(requestUrl, window.location.origin);
            isFirstParty =
                absoluteUrl.origin === window.location.origin;
        } catch {
            isFirstParty =
                requestUrl.startsWith('/') &&
                !requestUrl.startsWith('//');
        }

        if (isFirstParty) {
            // Separate Sentry issues by HTTP method and endpoint.
            const fingerprintMethod = String(
                error?.config?.method || 'GET'
            ).toUpperCase();

            let fingerprintEndpoint = requestUrl.split('#')[0].split('?')[0];

            try {
                fingerprintEndpoint = new URL(
                    requestUrl,
                    window.location.origin
                ).pathname;
            } catch {
                // Keep the fallback endpoint without query parameters.
            }

            event.fingerprint = [
                'axios-network-error',
                fingerprintMethod,
                fingerprintEndpoint || '/',
            ];

            const request = error?.request;

            const requestDetails = {
                code: error.code,
                message: error.message,
                method: error?.config?.method || null,
                url: requestUrl,
                baseURL,
                timeout: error?.config?.timeout ?? null,
                aborted: error?.config?.signal?.aborted ?? null,
                withCredentials: error?.config?.withCredentials ?? null,
                request: request
                    ? {
                        constructor: request?.constructor?.name || null,
                        readyState: typeof request?.readyState === 'number'
                            ? request.readyState
                            : null,
                        status: typeof request?.status === 'number'
                            ? request.status
                            : null,
                        responseURL: request?.responseURL || null,
                        responseType: request?.responseType || null,
                    }
                    : null,
            };

            const activeElement = document.activeElement;

            const activeElementContext = activeElement
                ? {
                    tag: activeElement.tagName,
                    id: activeElement.id || null,
                    className: activeElement.className || null,
                    type: activeElement.getAttribute?.('type') || null,
                    name: activeElement.getAttribute?.('name') || null,
                }
                : null;

            addTags({
                first_party_axios_network_error: 'true',
                ...(error?.config?.method
                    ? {
                        first_party_axios_method:
                            String(error.config.method).toUpperCase(),
                    }
                    : {}),
                ...(requestUrl
                    ? { first_party_axios_url: requestUrl.slice(0, 200) }
                    : {}),
            });

            addExtra({
                axiosNetworkError: requestDetails,
                browser: {
                    online: navigator.onLine,
                    visibilityState: document.visibilityState,
                    userAgent: navigator.userAgent,
                },
                page: {
                    url: location.href,
                    pathname: location.pathname,
                    referrer: document.referrer,
                },
                activeElement: activeElementContext,
                recentUiBreadcrumbs: getUiBreadcrumbs(10),
                recentNetworkBreadcrumbs: getNetworkBreadcrumbs(15),
                lastBreadcrumbs: getLastBreadcrumbs(30),
            });
        }
    }

    // ============================================================
    // TEMPORARY DIAGNOSTICS — remove after fixing the related bugs
    // ============================================================

    // Dynamic script appendChild SyntaxError: Unexpected identifier 'as'
    {
        const appendChildSyntaxText = [
            errorValue,
            message,
            error?.message,
            event?.message,
        ]
            .filter(value => typeof value === 'string')
            .join('\n');

        const isAppendChildSyntaxError =
            /Failed to execute ['"]appendChild['"] on ['"]Node['"][\s\S]*?Unexpected identifier ['"]as['"]/i
                .test(appendChildSyntaxText);

        if (isAppendChildSyntaxError) {
            const describeUrl = value => {
                if (typeof value !== 'string' || !value) return null;

                try {
                    const url = new URL(value, location.href);

                    return {
                        origin: url.origin,
                        pathname: url.pathname,
                        sameOrigin: url.origin === location.origin,
                    };
                } catch {
                    return null;
                }
            };

            const scriptInventory = [];
            const roots = [{ root: document, scope: 'document', component: null }];
            const visitedRoots = new Set();
            let totalScripts = 0;

            for (let i = 0; i < roots.length && i < 60; i++) {
                const { root, scope, component } = roots[i];

                if (!root || visitedRoots.has(root)) continue;
                visitedRoots.add(root);

                try {
                    const scripts = root.querySelectorAll('script');
                    totalScripts += scripts.length;

                    for (const script of scripts) {
                        if (scriptInventory.length >= 100) break;

                        const source = script.src ? '' : (script.textContent || '');
                        const asOffsets = [];
                        let asCount = 0;

                        if (source.length <= 200000) {
                            const asRegex = /\bas\b/g;
                            let match;

                            while ((match = asRegex.exec(source)) !== null) {
                                asCount++;

                                if (asOffsets.length < 8) {
                                    asOffsets.push(match.index);
                                }
                            }
                        }

                        scriptInventory.push({
                            scope,
                            component,
                            src: describeUrl(script.src),
                            type: script.type || 'classic',
                            inlineLength: script.src ? null : source.length,
                            scanned: !script.src && source.length <= 200000,
                            asTokenCount: asCount,
                            asTokenOffsets: asOffsets,
                            hasModuleKeywords: /\b(?:import|export)\b/.test(source),
                            hasPossibleTypeScriptAssertion:
                                /\bas\s+(?:const|[A-Z][$\w]*(?:\s*<[^>\n]{1,80}>)?)/.test(source),
                        });
                    }

                    const hosts = root.querySelectorAll('a-component');

                    for (const host of hosts) {
                        if (roots.length >= 60) break;

                        if (host.shadowRoot) {
                            roots.push({
                                root: host.shadowRoot,
                                scope: 'component-shadow',
                                component: host.getAttribute('template')?.split(/[?#]/)[0].slice(0, 160) || null,
                            });
                        }
                    }
                } catch {
                    // DOM inspection must not interfere with Sentry capture.
                }
            }

            let recentResources = [];

            try {
                recentResources = performance.getEntriesByType('resource')
                    .filter(resource =>
                        resource.initiatorType === 'script' ||
                        /\/components\/.*\.html(?:[?#]|$)/i.test(resource.name) ||
                        /\.m?js(?:[?#]|$)/i.test(resource.name)
                    )
                    .slice(-40)
                    .map(resource => ({
                        url: describeUrl(resource.name),
                        initiatorType: resource.initiatorType,
                        startTime: Math.round(resource.startTime),
                        duration: Math.round(resource.duration),
                        transferSize: resource.transferSize,
                        responseStatus: resource.responseStatus ?? null,
                    }));
            } catch {
                // Resource Timing may be unavailable.
            }

            const recentBreadcrumbs = getLastBreadcrumbs(70)
                .filter(breadcrumb =>
                    ['fetch', 'xhr', 'console', 'component.fetch-diagnostic', 'alpine.component-lifecycle'].includes(breadcrumb?.category)
                )
                .slice(-35)
                .map(breadcrumb => ({
                    category: breadcrumb.category,
                    level: breadcrumb.level,
                    message: typeof breadcrumb.message === 'string'
                        ? breadcrumb.message.slice(0, 300)
                        : null,
                    timestamp: breadcrumb.timestamp,
                    method: breadcrumb.data?.method ?? null,
                    statusCode: breadcrumb.data?.status_code ?? null,
                    url: describeUrl(breadcrumb.data?.url),
                    template: typeof breadcrumb.data?.template === 'string'
                        ? breadcrumb.data.template.split(/[?#]/)[0].slice(0, 160)
                        : null,
                }));

            addTags({
                append_child_syntax_error: 'true',
                append_child_inline_as_candidates: scriptInventory.some(script =>
                    script.asTokenCount > 0
                ) ? 'true' : 'false',
            });

            addExtra({
                appendChildSyntaxDiagnostics: {
                    error: {
                        type: exception?.type || error?.name || null,
                        value: String(errorValue || message).slice(0, 1000),
                        originalMessage: error?.message || null,
                        originalStack: typeof error?.stack === 'string'
                            ? error.stack.slice(0, 3000)
                            : null,
                        frames: frames.slice(-15).map(getFrameContext),
                    },
                    page: getPageContext(),
                    browser: getBrowserContext(),
                    scriptsFound: totalScripts,
                    scriptsInspected: scriptInventory.length,
                    rootsInspected: visitedRoots.size,
                    scriptCandidates: scriptInventory.filter(script =>
                        script.asTokenCount > 0 || script.hasPossibleTypeScriptAssertion
                    ).slice(0, 30),
                    recentResources,
                    recentBreadcrumbs,
                    limitation: 'DOM inventory only: a failed or detached script may not be present',
                },
            });
        }
    }

    // UnhandledRejection: object captured with no keys
    {
        const rejectionText = [
            errorValue,
            message,
            error?.message,
        ]
            .filter(value => typeof value === 'string')
            .join('\n');

        const isEmptyObjectRejection =
            /Object captured as promise rejection with keys:\s*\[object has no keys\]/i
                .test(rejectionText);

        if (isEmptyObjectRejection) {
            const describeRejectedValue = value => {
                if (value === null || value === undefined) {
                    return {
                        type: value === null ? 'null' : 'undefined',
                    };
                }

                const type = typeof value;

                if (type !== 'object' && type !== 'function') {
                    return {
                        type,
                        value: ['string', 'number', 'boolean'].includes(type)
                            ? String(value).slice(0, 300)
                            : null,
                    };
                }

                let constructor = null;
                let objectTag = null;
                let keys = [];
                let propertyNames = [];
                let symbols = [];
                let properties = [];

                try {
                    const prototype = Object.getPrototypeOf(value);
                    const descriptor = prototype
                        ? Object.getOwnPropertyDescriptor(prototype, 'constructor')
                        : null;

                    if (typeof descriptor?.value === 'function') {
                        constructor = descriptor.value.name || null;
                    }
                } catch {}

                try {
                    objectTag = Object.prototype.toString.call(value);
                } catch {}

                try {
                    keys = Object.keys(value).slice(0, 50);
                } catch {}

                try {
                    propertyNames = Object.getOwnPropertyNames(value).slice(0, 50);
                } catch {}

                try {
                    symbols = Object.getOwnPropertySymbols(value)
                        .slice(0, 20)
                        .map(symbol => String(symbol).slice(0, 150));
                } catch {}

                properties = propertyNames.slice(0, 30).map(name => {
                    try {
                        const descriptor = Object.getOwnPropertyDescriptor(value, name);

                        if (!descriptor) {
                            return { name, unavailable: true };
                        }

                        const hasValue =
                            Object.prototype.hasOwnProperty.call(descriptor, 'value');

                        const propertyValue = descriptor.value;

                        return {
                            name,
                            enumerable: descriptor.enumerable,
                            kind: hasValue ? 'data' : 'accessor',
                            valueType: hasValue
                                ? propertyValue === null
                                    ? 'null'
                                    : Array.isArray(propertyValue)
                                        ? 'array'
                                        : typeof propertyValue
                                : null,
                        };
                    } catch {
                        return { name, unavailable: true };
                    }
                });

                return {
                    type,
                    constructor,
                    objectTag,
                    isArray: Array.isArray(value),
                    isError: value instanceof Error,
                    enumerableKeys: keys,
                    ownPropertyNames: propertyNames,
                    symbols,
                    properties,
                    keyCount: keys.length,
                };
            };

            let rejectionReason;

            try {
                rejectionReason = error?.reason;
            } catch {
                rejectionReason = undefined;
            }

            addTags({
                unhandled_rejection_diagnostic: 'true',
                empty_object_promise_rejection: 'true',
            });

            addExtra({
                unhandledRejectionDiagnostics: {
                    eventType: exception?.type || null,
                    eventValue: errorValue.slice(0, 1000),

                    originalException: describeRejectedValue(error),
                    rejectionReason: describeRejectedValue(rejectionReason),
                    syntheticException: describeRejectedValue(
                        hint?.syntheticException
                    ),

                    originalExceptionDetails: getExceptionDetails(error),
                    syntheticExceptionDetails: getExceptionDetails(
                        hint?.syntheticException
                    ),

                    topFrame: getFrameContext(topFrame),
                    stackFrames: frames.map(getFrameContext),

                    page: getPageContext(),
                    browser: getBrowserContext(true),
                    navigation: getNavigationContext(),

                    recentUiBreadcrumbs: getUiBreadcrumbs(40, true),
                    recentNetworkBreadcrumbs: getNetworkBreadcrumbs(30),
                    lastBreadcrumbs: getLastBreadcrumbs(50),

                    scripts: getScriptSources().slice(0, 100),
                    jsResources: getResourceContext(
                        resource => /\.js(?:\?|$)/i.test(resource.name),
                        30
                    ),
                },
            });
        }
    }

    // Minified / opaque production errors
    if (exception?.type === 'Error' && /^[A-Za-z]{1,3}$/.test(errorValue)) {
        const scriptSources = getScriptSources();

        const matchingScripts = topFrame?.filename
            ? scriptSources.filter(src => src.includes(topFrame.filename))
            : [];

        const matchingResources = topFrame?.filename
            ? getResourceContext(
                entry => entry.name.includes(topFrame.filename),
                10
            )
            : [];

        const recentUiBreadcrumbs = getUiBreadcrumbs(20, true);
        const recentNetworkBreadcrumbs = getNetworkBreadcrumbs(20);
        const activeElement = document.activeElement;

        const activeElementContext = activeElement
            ? {
                tag: activeElement.tagName,
                id: activeElement.id || null,
                className: activeElement.className || null,
                type: activeElement.getAttribute?.('type') || null,
                name: activeElement.getAttribute?.('name') || null,
                selector: (() => {
                    try {
                        if (activeElement.matches?.('input, textarea, select, button')) {
                            const tag = activeElement.tagName.toLowerCase();
                            const id = activeElement.id ? `#${activeElement.id}` : '';
                            const attribute =
                                activeElement.tagName === 'BUTTON' ? 'type' : 'name';
                            const value =
                                activeElement.getAttribute?.('name') ||
                                activeElement.getAttribute?.('type') ||
                                '';

                            return `${tag}${id}[${attribute}="${value}"]`;
                        }

                        return activeElement.tagName.toLowerCase();
                    } catch {
                        return null;
                    }
                })(),
            }
            : null;

        const alpine =
            typeof window !== 'undefined'
                ? window.Alpine
                : undefined;

        const alpineRoot = activeElement?.closest?.('[x-data]');

        const alpineContext = alpineRoot
            ? {
                tag: alpineRoot.tagName,
                id: alpineRoot.id || null,
                className: alpineRoot.className || null,
                xData: alpineRoot.getAttribute('x-data')?.slice(0, 2000) || null,
                outerHTML: alpineRoot.outerHTML.slice(0, 4000),
            }
            : null;

        const alpineComponents = Array.from(
            document.querySelectorAll('[x-data]')
        ).map((el, index) => ({
            index,
            tag: el.tagName,
            id: el.id || null,
            className: el.className || null,
            xData: el.getAttribute('x-data')?.slice(0, 1000) || null,
        }));

        const relevantAlpineComponents = Array.from(
            document.querySelectorAll('[x-data]')
        )
            .filter(el => {
                const xData = el.getAttribute('x-data') || '';

                return (
                    activeElement === el ||
                    el.contains(activeElement) ||
                    /stepEmail|notifyData/i.test(xData)
                );
            })
            .slice(0, 10)
            .map((el, index) => {
                let dataKeys = null;

                try {
                    dataKeys = typeof alpine?.$data === 'function'
                        ? Object.keys(alpine.$data(el))
                        : null;
                } catch {
                    dataKeys = null;
                }

                return {
                    index,
                    tag: el.tagName,
                    id: el.id || null,
                    className: el.className || null,
                    xData: el.getAttribute('x-data')?.slice(0, 2000) || null,
                    dataKeys,
                    outerHTML: el.outerHTML.slice(0, 4000),
                };
            });

        const rejectionDetails = {
            type: typeof error,
            constructor: error?.constructor?.name || null,
            name: error?.name || null,
            message: error?.message || null,
            stack: error?.stack || null,
            reason: error?.reason
                ? {
                    type: typeof error.reason,
                    constructor: error.reason?.constructor?.name || null,
                    name: error.reason?.name || null,
                    message: error.reason?.message || null,
                    stack: error.reason?.stack || null,
                    keys: typeof error.reason === 'object'
                        ? Object.keys(error.reason)
                        : null,
                }
                : null,
        };

        addTags({
            minified_error: 'true',
            ...(topFrame?.filename
                ? {
                    minified_error_source:
                        /(?:\/build\/|\/js\/|webpack|chunk|bundle|app)/i.test(
                            topFrame.filename
                        )
                            ? 'application_bundle'
                            : 'other',
                }
                : {}),
        });

        addExtra({
            exception: {
                type: exception.type,
                value: exception.value,
                mechanism: exception.mechanism,
            },
            rejectionDetails,
            topFrame: getFrameContext(topFrame),
            stackFrames: frames.map(getFrameContext),
            originalException: getExceptionDetails(error),
            syntheticException: getExceptionDetails(hint?.syntheticException),
            page: getPageContext(),
            browser: getBrowserContext(true),
            viewport: {
                width: window.innerWidth,
                height: window.innerHeight,
                devicePixelRatio: window.devicePixelRatio,
            },
            navigation: getNavigationContext(),
            scripts: {
                total: scriptSources.length,
                matchingTopFrame: matchingScripts,
                all: scriptSources,
            },
            matchingResources,
            alpineComponents,
            relevantAlpineComponents,
            activeElementContext,
            alpineContext,
            recentUiBreadcrumbs,
            recentNetworkBreadcrumbs,
            lastBreadcrumbs: getLastBreadcrumbs(30),
        });
    }

    // Axios timeouts
    if (error?.isAxiosError && (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout'))) {
        addTags({ axios_timeout: 'true' });

        addExtra({
            axiosTimeout: {
                method: error?.config?.method,
                requestUrl: error?.config?.url,
                timeout: error?.config?.timeout,
                code: error?.code,
                message: error?.message,
                aborted: error?.config?.signal?.aborted,
            },
            network: {
                online: navigator.onLine,
                visibilityState: document.visibilityState,
            },
            page: {
                url: location.href,
                pathname: location.pathname,
                referrer: document.referrer,
            },
            browser: {
                userAgent: navigator.userAgent,
                language: navigator.language,
            },
            lastBreadcrumbs: getLastBreadcrumbs(20),
        });
    }

    // Safari: Importing a module script failed
    if (errorValue.includes('Importing a module script failed')) {
        addTags({
            safari_module_import_error: 'true',
        });

        try {
            const eventTime = typeof event.timestamp === 'number'
                ? event.timestamp * 1000
                : Date.now();

            // Module scripts currently present in the DOM.
            const moduleScripts = Array.from(
                document.querySelectorAll('script[type="module"]')
            )
                .slice(-30)
                .map(script => ({
                    src: describeSentryModuleUrl(script.src),
                    inline: !script.src,
                    async: script.async,
                    crossOrigin: script.crossOrigin || null,
                    integrity: Boolean(script.integrity),
                    connected: script.isConnected,
                }));

            // Module preload declarations.
            const modulePreloads = Array.from(
                document.querySelectorAll('link[rel~="modulepreload"]')
            )
                .slice(-30)
                .map(link => ({
                    href: describeSentryModuleUrl(link.href),
                    crossOrigin: link.crossOrigin || null,
                    integrity: Boolean(link.integrity),
                    connected: link.isConnected,
                }));

            // Recent JS-related resource timing entries.
            let resources = [];

            try {
                resources = performance
                    .getEntriesByType('resource')
                    .filter(resource =>
                        resource.initiatorType === 'script' ||
                        /\.m?js(?:[?#]|$)/i.test(resource.name)
                    )
                    .slice(-50)
                    .map(resource => ({
                        url: describeSentryModuleUrl(resource.name),
                        initiatorType: resource.initiatorType,
                        startTime: Math.round(resource.startTime),
                        duration: Math.round(resource.duration),
                        ageMs: Math.round(
                            performance.now() -
                            resource.startTime -
                            resource.duration
                        ),
                        responseStatus:
                            typeof resource.responseStatus === 'number'
                                ? resource.responseStatus
                                : null,
                        transferSize:
                            typeof resource.transferSize === 'number'
                                ? resource.transferSize
                                : null,
                        encodedBodySize:
                            typeof resource.encodedBodySize === 'number'
                                ? resource.encodedBodySize
                                : null,
                        decodedBodySize:
                            typeof resource.decodedBodySize === 'number'
                                ? resource.decodedBodySize
                                : null,
                    }));
            } catch {
                resources = [];
            }

            // Module resource load failures observed before the error.
            const resourceFailures = sentryModuleLoadFailures
                .filter(item =>
                    item.timestamp <= eventTime + 1000 &&
                    item.timestamp >= eventTime - 15000
                )
                .slice(-15)
                .map(item => ({
                    ...item,
                    beforeErrorMs: eventTime - item.timestamp,
                }));

            // Stack frames from Sentry.
            const stackFrames = frames
                .slice(-15)
                .map(getFrameContext);

            const topModuleFrame = stackFrames.length
                ? stackFrames[stackFrames.length - 1]
                : null;

            // Last relevant network events.
            const recentNetwork = getNetworkBreadcrumbs(20);

            const recentUi = getUiBreadcrumbs(15);

            // Current navigation context.
            const navigation = {
                reason: navigationState.reason,
                elapsedMs: navigationState.timestamp
                    ? eventTime - navigationState.timestamp
                    : null,
            };

            // Current document state.
            const documentContext = {
                readyState: document.readyState,
                visibilityState: document.visibilityState,
                baseURI: describeSentryModuleUrl(
                    document.baseURI
                ),
            };

            // Diagnostic classification tags.
            addTags({
                safari_module_resource_failure_seen:
                    resourceFailures.length > 0 ? 'true' : 'false',

                safari_module_dom_script_present:
                    moduleScripts.length > 0 ? 'true' : 'false',

                safari_module_stack_available:
                    stackFrames.length > 0 ? 'true' : 'false',
            });

            addExtra({
                moduleImport: {
                    error: {
                        type: exception?.type || null,
                        value: errorValue,
                        originalName: error?.name || null,
                        originalMessage: error?.message || null,
                        originalStack: error?.stack || null,
                        mechanism:
                            exception?.mechanism?.type || null,
                    },

                    topFrame: topModuleFrame,
                    stackFrames,

                    moduleScripts,
                    modulePreloads,

                    resourceFailures,
                    resources,

                    recentNetwork,
                    recentUi,

                    navigation,
                    document: documentContext,
                },

                page: {
                    url: location.href,
                    pathname: location.pathname,
                    referrer: document.referrer,
                },

                browser: {
                    userAgent: navigator.userAgent,
                    online: navigator.onLine,
                    visibilityState: document.visibilityState,
                },

                breadcrumbs: getLastBreadcrumbs(20),
            });
        } catch {
            // Diagnostic collection must not prevent Sentry reporting.
        }
    }

    // Printer compatibility mapping diagnostics
    {
        const compatibilityError =
            /Cannot read properties of undefined \(reading ['"](?:icon|type)['"]\)/i
                .test(errorValue);

        const compatibilityBreadcrumbs = getBreadcrumbs()
            .map(breadcrumb => ({
                breadcrumb,
                text: getBreadcrumbText(breadcrumb),
            }))
            .filter(item =>
                /compatibilities\s*\[\s*item\.material_category\s*\]/i
                    .test(item.text)
            )
            .slice(-20);

        if (compatibilityError && compatibilityBreadcrumbs.length > 0) {
            const alpine =
                typeof window !== 'undefined'
                    ? window.Alpine
                    : undefined;

            const safeRead = (object, key) => {
                try {
                    return object == null ? undefined : object[key];
                } catch {
                    return undefined;
                }
            };

            const safeKeys = (value, limit = 100) => {
                try {
                    if (
                        value &&
                        (typeof value === 'object' || typeof value === 'function')
                    ) {
                        return Object.keys(value).slice(0, limit);
                    }
                } catch {
                    // A reactive proxy may not be inspectable.
                }

                return [];
            };

            const safeValue = value => {
                try {
                    if (
                        value === null ||
                        value === undefined ||
                        ['string', 'number', 'boolean'].includes(typeof value)
                    ) {
                        return value;
                    }

                    if (typeof value === 'bigint') {
                        return String(value);
                    }

                    if (Array.isArray(value)) {
                        return `[Array(${value.length})]`;
                    }

                    if (typeof value === 'object') {
                        return '[object]';
                    }

                    return String(value);
                } catch {
                    return '[unavailable]';
                }
            };

            const inspectScope = (scope, index) => {
                if (!scope) {
                    return null;
                }

                try {
                    const item = safeRead(scope, 'item');
                    const compatibilities = safeRead(scope, 'compatibilities');
                    const category = safeRead(item, 'material_category');
                    const entry = category == null
                        ? undefined
                        : safeRead(compatibilities, category);

                    let itemDetails = null;

                    if (item && typeof item === 'object') {
                        itemDetails = {
                            keys: safeKeys(item, 60),
                            id: safeValue(safeRead(item, 'id')),
                            name: safeValue(safeRead(item, 'name')),
                            material_category: safeValue(category),
                            material_category_type: typeof category,
                        };
                    }

                    let compatibilityEntry = null;

                    if (entry != null) {
                        compatibilityEntry = {
                            keys: safeKeys(entry, 60),
                            icon: safeValue(safeRead(entry, 'icon')),
                            type: safeValue(safeRead(entry, 'type')),
                        };
                    }

                    return {
                        index,
                        scopeKeys: safeKeys(scope, 80),
                        hasItem: item !== undefined,
                        item: itemDetails,
                        compatibilitiesType:
                            compatibilities === null
                                ? 'null'
                                : typeof compatibilities,
                        compatibilitiesKeys: safeKeys(compatibilities),
                        categoryKeyFound:
                            category != null &&
                            entry !== undefined,
                        compatibilityEntry,
                    };
                } catch (inspectionError) {
                    return {
                        index,
                        inspectError:
                            inspectionError?.message || String(inspectionError),
                    };
                }
            };

            const inspectElement = element => {
                const scopes = [];
                const seenScopes = new Set();

                const addScope = scope => {
                    if (
                        scope &&
                        !seenScopes.has(scope) &&
                        ['object', 'function'].includes(typeof scope)
                    ) {
                        seenScopes.add(scope);
                        scopes.push(scope);
                    }
                };

                let current = element;
                let depth = 0;

                while (current && depth < 10) {
                    try {
                        if (Array.isArray(current._x_dataStack)) {
                            current._x_dataStack.forEach(addScope);
                        }
                    } catch {
                        // Alpine internal scope unavailable.
                    }

                    try {
                        if (typeof alpine?.$data === 'function') {
                            addScope(alpine.$data(current));
                        }
                    } catch {
                        // The scope may be unavailable during initialization.
                    }

                    current = current.parentElement;
                    depth++;
                }

                const attributes = Array.from(element.attributes || [])
                    .filter(attribute =>
                        /compatibilities|material_category/i.test(attribute.value)
                    )
                    .slice(0, 20)
                    .map(attribute => ({
                        name: attribute.name,
                        value: attribute.value.slice(0, 1000),
                    }));

                return {
                    element: {
                        tag: element.tagName,
                        id: element.id || null,
                        className:
                            typeof element.className === 'string'
                                ? element.className.slice(0, 300)
                                : null,
                        connected: element.isConnected,
                        attributes,
                        outerHTML: element.outerHTML?.slice(0, 2000) || null,
                    },
                    scopes: scopes.map(inspectScope),
                };
            };

            // Find DOM elements whose Alpine expressions use this mapping.
            const matchingElements = [];
            const allElements = document.querySelectorAll('*');

            for (let i = 0; i < allElements.length; i++) {
                const element = allElements[i];
                const attributes = element.attributes;

                let matched = false;

                for (let j = 0; j < attributes.length; j++) {
                    if (
                        /compatibilities|material_category/i.test(
                            attributes[j].value
                        )
                    ) {
                        matched = true;
                        break;
                    }
                }

                if (matched) {
                    matchingElements.push(element);
                }

                if (matchingElements.length >= 20) {
                    break;
                }
            }

            addTags({
                printer_compatibility_mapping_error: 'true',
            });

            addExtra({
                printerCompatibilityDiagnostics: {
                    error: errorValue,

                    expressions: compatibilityBreadcrumbs.map(item => ({
                        category: item.breadcrumb?.category,
                        message: item.breadcrumb?.message,
                        text: item.text.slice(0, 1500),
                        timestamp: item.breadcrumb?.timestamp,
                    })),

                    matchingElementsFound: matchingElements.length,

                    elements: matchingElements.map(inspectElement),

                    page: {
                        url: location.href,
                        pathname: location.pathname,
                    },

                    browser: {
                        userAgent: navigator.userAgent,
                        online: navigator.onLine,
                        visibilityState: document.visibilityState,
                    },
                },
            });
        }
    }

    // Generic Alpine expression errors
    if (message.includes('Alpine Expression Error')) {
        const alpineExpression = message.match(/Expression:\s*"([\s\S]*?)"\s*(?:\n|$)/)?.[1] || null;

        addTags({ alpine_expression_error: 'true' });

        addExtra({
            alpine: {
                expression: alpineExpression,
                message,
                pathname: location.pathname,
                visibilityState: document.visibilityState,
                readyState: document.readyState,
            },
            lastBreadcrumbs: getLastBreadcrumbs(30),
        });
    }

    // Firefox global onerror
    {
        const exceptionValues = event.exception?.values || [];

        const allExceptionText = [
            message,
            errorValue,
            error?.name,
            error?.message,
            event.message,
            event.logentry?.message,

            ...exceptionValues.flatMap(value => [
                value?.type,
                value?.value,
            ]),
        ]
            .filter(value => typeof value === 'string')
            .join('\n');

        const mechanismText = exceptionValues
            .map(value => {
                try {
                    return JSON.stringify(value?.mechanism || {});
                } catch {
                    return '';
                }
            })
            .join('\n');

        const isFirefox =
            /Firefox\//i.test(userAgent) ||
            /firefox/i.test(event.contexts?.browser?.name || '');

        const isGlobalOnError =
            /onerror/i.test(mechanismText) ||
            /global_handlers\.onerror/i.test(
                String(event.tags?.mechanism || '')
            );

        const isNsErrorFailure =
            /NS_ERROR_FAILURE/i.test(allExceptionText);

        if (isFirefox && (isGlobalOnError || isNsErrorFailure)) {
            addTags({
                firefox_global_onerror_diagnostic: 'true',

                ...(isNsErrorFailure
                    ? { firefox_ns_error_failure: 'true' }
                    : {}),
            });

            addExtra({
                firefoxGlobalOnErrorDiagnostics: {
                    errorText: allExceptionText.slice(0, 3000),
                    mechanismText: mechanismText.slice(0, 2000),

                    exceptionValues: exceptionValues.map(value => ({
                        type: value?.type || null,
                        value: value?.value || null,
                        mechanism: value?.mechanism || null,
                        frames: (value?.stacktrace?.frames || [])
                            .slice(-10)
                            .map(getFrameContext),
                    })),

                    originalException: getExceptionDetails(error),
                    syntheticException: getExceptionDetails(
                        hint?.syntheticException
                    ),

                    page: getPageContext(),
                    browser: getBrowserContext(true),
                    navigation: getNavigationContext(),

                    scripts: getScriptSources().slice(0, 100),
                    recentNetworkBreadcrumbs: getNetworkBreadcrumbs(30),
                    lastBreadcrumbs: getLastBreadcrumbs(40),
                },
            });
        }
    }

    // Unexpected end of script
    if (errorValue.includes('Unexpected end of script')) {
        addTags({ unexpected_end_of_script: 'true' });

        addExtra({
            exceptionDetails: {
                name: error?.name,
                message: error?.message,
                stack: error?.stack,
            },
            scripts: Array.from(document.scripts)
                .map(script => ({
                    src: script.src || null,
                    type: script.type || null,
                    async: script.async,
                    defer: script.defer,
                }))
                .filter(script => script.src),
            jsResources: getResourceContext(
                resource => /\.js(\?|$)/i.test(resource.name),
                40
            ),
            page: {
                url: location.href,
                referrer: document.referrer,
            },
            lastBreadcrumbs: getLastBreadcrumbs(30),
        });
    }

    // Array.from invalid input
    if (errorValue.includes('Array.from requires an array-like object')) {
        addTags({ array_from_invalid_input: 'true' });

        addExtra({
            exceptionDetails: {
                name: error?.name,
                message: error?.message,
                stack: error?.stack,
            },
            page: {
                url: location.href,
                pathname: location.pathname,
            },
            lastBreadcrumbs: getLastBreadcrumbs(20),
        });
    }

    // Null .id
    if (errorValue.includes("Cannot read properties of null (reading 'id')")) {
        addTags({ null_element_id: 'true' });

        addExtra({
            originalException: getExceptionDetails(error),
            sentryException: {
                type: exception?.type,
                value: exception?.value,
                frames: exception?.stacktrace?.frames || [],
            },
            page: {
                url: location.href,
                pathname: location.pathname,
            },
            lastBreadcrumbs: getLastBreadcrumbs(20),
        });
    }

    // Undefined .protocol
    if (errorValue.includes("Cannot read properties of undefined (reading 'protocol')")) {
        addTags({ undefined_protocol: 'true' });

        addExtra({
            originalException: getExceptionDetails(error),
            sentryException: {
                type: exception?.type,
                value: exception?.value,
                frames: exception?.stacktrace?.frames || [],
            },
            axios: error?.isAxiosError
                ? {
                    code: error.code,
                    message: error.message,
                    url: error.config?.url,
                    method: error.config?.method,
                    baseURL: error.config?.baseURL,
                }
                : null,
            lastBreadcrumbs: getLastBreadcrumbs(30),
        });
    }

    // Alpine undefined variable / intermittent scope errors
    if (exception?.type === 'ReferenceError' && /is not defined$/i.test(errorValue)) {
        const variable = errorValue.match(/^(.+?) is not defined$/i)?.[1] || null;

        const target =
            typeof error?.el?.getAttribute === 'function'
                ? error.el
                : null;

        const scopeRoot = target?.closest?.('[x-data]') || null;

        const alpineErrorBreadcrumbs = getBreadcrumbs()
            .filter(b => {
                const args = Array.isArray(b?.data?.arguments)
                    ? b.data.arguments
                        .filter(value => typeof value === 'string')
                        .join('\n')
                    : '';

                return b?.category === 'console' &&
                    /Alpine Expression Error|is not defined/.test(
                        `${b?.message || ''}\n${args}`
                    );
            })
            .slice(-60)
            .map(b => ({
                timestamp: b.timestamp,
                message: String(
                    b?.message || b?.data?.arguments?.[0] || ''
                ).slice(0, 1000),
            }));

        const missingVariableCounts = {};

        for (const breadcrumb of alpineErrorBreadcrumbs) {
            const matches = getRegexMatches(
                breadcrumb.message,
                /([A-Za-z_$][\w$]*) is not defined/g
            );

            matches.forEach(match => {
                missingVariableCounts[match[1]] =
                    (missingVariableCounts[match[1]] || 0) + 1;
            });
        }

        const variables = [
            ...new Set([
                variable,
                ...Object.keys(missingVariableCounts),
            ].filter(Boolean)),
        ].slice(0, 30);

        const alpine =
            typeof window !== 'undefined'
                ? window.Alpine
                : undefined;

        const inspectScope = el => {
            if (!el) {
                return null;
            }

            const inspectLayer = (layer, index) => {
                try {
                    return {
                        index,
                        keys: Object.keys(layer || {}).slice(0, 80),
                        variables: Object.fromEntries(
                            variables.map(name => {
                                let exists = false;

                                try {
                                    exists = Boolean(layer) && name in layer;
                                } catch {
                                    // A scope proxy may reject inspection.
                                }

                                return [name, exists];
                            })
                        ),
                    };
                } catch {
                    return { index, inspectFailed: true };
                }
            };

            const dataStack = Array.isArray(el._x_dataStack)
                ? el._x_dataStack
                : null;

            let resolvedData = null;

            try {
                if (typeof alpine?.$data === 'function') {
                    resolvedData = alpine.$data(el);
                }
            } catch {
                // Scope resolution can fail during Alpine updates.
            }

            return {
                connected: el.isConnected,
                hasXData: el.hasAttribute('x-data'),
                xData: el.getAttribute('x-data')?.slice(0, 1500) || null,
                xRef: el.getAttribute('x-ref'),
                alpineMarker: el._x_marker || null,
                hasDataStack: Boolean(dataStack),
                dataStack: dataStack
                    ? dataStack.slice(0, 10).map(inspectLayer)
                    : null,
                resolvedData: resolvedData
                    ? inspectLayer(resolvedData, 'resolved')
                    : null,
            };
        };

        const describeElement = el => {
            if (!el) {
                return null;
            }

            const matchedAttributes = Array.from(el.attributes || [])
                .filter(attr =>
                    variables.some(name => attr.value.includes(name))
                )
                .map(attr => ({
                    name: attr.name,
                    value: attr.value.slice(0, 1000),
                }))
                .slice(0, 20);

            return {
                tag: el.tagName,
                id: el.id || null,
                className: typeof el.className === 'string'
                    ? el.className.slice(0, 300)
                    : null,
                connected: el.isConnected,
                xData: el.getAttribute('x-data')?.slice(0, 1500) || null,
                xRef: el.getAttribute('x-ref'),
                matchedAttributes,
                componentTemplate:
                    el.closest('a-component')?.getAttribute('template') || null,
                scope: inspectScope(el),
                outerHTML: el.outerHTML?.slice(0, 2000) || null,
            };
        };

        const ancestorChain = [];
        let current = target;

        for (let i = 0; current && i < 10; i++) {
            ancestorChain.push(describeElement(current));
            current = current.parentElement;
        }

        const scanRoot =
            scopeRoot?.parentElement ||
            target?.parentElement ||
            document.body;

        const candidates = scanRoot
            ? Array.from(
                scanRoot.querySelectorAll(
                    '[x-data], [x-ref], [x-init], [x-for], [x-if], [x-show], [x-text], [x-html], [x-model], [x-bind]'
                )
            ).slice(0, 600)
            : [];

        if (target && !candidates.includes(target)) {
            candidates.unshift(target);
        }

        const relevantElements = candidates
            .filter(el =>
                el === target ||
                Array.from(el.attributes || []).some(attr =>
                    variables.some(name => attr.value.includes(name))
                )
            )
            .slice(0, 30)
            .map(describeElement);

        addTags({ alpine_reference_error: 'true' });

        addExtra({
            alpineReferenceError: {
                variable,
                expression: error?.expression || null,
                target: describeElement(target),
                scopeRoot: describeElement(scopeRoot),
                ancestorChain,
                relevantElements,
                missingVariableCounts,
                alpineErrorBreadcrumbs,
                page: {
                    url: location.href,
                    pathname: location.pathname,
                },
                stack: error?.stack || null,
                lastBreadcrumbs: getLastBreadcrumbs(30),
            },
        });
    }

    // JSON.parse(undefined)
    if (errorValue === '"undefined" is not valid JSON') {
        const storage = {
            localStorageKeys: getSafeStorageKeys(localStorage),
            sessionStorageKeys: getSafeStorageKeys(sessionStorage),
        };

        addTags({
            invalid_json: 'true',
            invalid_json_value: 'undefined',
        });

        addExtra({
            exception: {
                type: exception?.type,
                value: exception?.value,
                mechanism: exception?.mechanism,
                frames: exception?.stacktrace?.frames || [],
            },
            originalException: getExceptionDetails(error),
            storage,
            scripts: getScriptSources(),
            page: getPageContext(),
            recentUiBreadcrumbs: getUiBreadcrumbs(20),
            recentNetworkBreadcrumbs: getNetworkBreadcrumbs(20),
            lastBreadcrumbs: getLastBreadcrumbs(30),
        });
    }

    // Undefined backgroundQueue
    if (errorValue.includes("Cannot read properties of undefined (reading 'add')")) {
        const queueBreadcrumbs = getBreadcrumbs()
            .filter(b =>
                getBreadcrumbText(b).includes('backgroundQueue.add')
            )
            .slice(-10)
            .map(b => ({
                category: b.category,
                message: String(b.message || '').slice(0, 2000),
                arguments: Array.isArray(b?.data?.arguments)
                    ? b.data.arguments
                        .filter(value => typeof value === 'string')
                        .map(value => value.slice(0, 3000))
                    : [],
            }));

        if (queueBreadcrumbs.length) {
            const root = document.querySelector('[x-data="feedbackListing"]');
            const alpine =
                typeof window !== 'undefined'
                    ? window.Alpine
                    : undefined;

            const getScopeInfo = el => {
                try {
                    if (typeof alpine?.$data !== 'function') {
                        return { alpineAvailable: false };
                    }

                    const data = alpine.$data(el);
                    const queue = data?.backgroundQueue;

                    return {
                        alpineAvailable: true,
                        backgroundQueueType:
                            queue === null ? 'null' : typeof queue,
                        backgroundQueueConstructor:
                            queue?.constructor?.name || null,
                        backgroundQueueHasAdd:
                            typeof queue?.add === 'function',
                        dataKeys: Object.keys(data).slice(0, 80),
                    };
                } catch (inspectionError) {
                    return {
                        inspectError:
                            inspectionError?.message || String(inspectionError),
                    };
                }
            };

            const scopeNodes = root
                ? [root, ...root.querySelectorAll('[x-data]')]
                : Array.from(document.querySelectorAll('[x-data]'));

            const alpineScopes = scopeNodes
                .slice(0, 100)
                .map((el, index) => {
                    const parentChain = [];
                    let parent = el.parentElement;

                    while (parent && parentChain.length < 8) {
                        if (
                            parent.hasAttribute('x-data') ||
                            parent.matches('a-component')
                        ) {
                            parentChain.push({
                                tag: parent.tagName,
                                id: parent.id || null,
                                xData: parent.getAttribute('x-data')?.slice(0, 500) || null,
                                template: parent.getAttribute('template'),
                                connected: parent.isConnected,
                            });
                        }

                        parent = parent.parentElement;
                    }

                    return {
                        index,
                        tag: el.tagName,
                        id: el.id || null,
                        className: typeof el.className === 'string'
                            ? el.className.slice(0, 300)
                            : null,
                        xData: el.getAttribute('x-data')?.slice(0, 500) || null,
                        connected: el.isConnected,
                        componentTemplate:
                            el.closest('a-component')?.getAttribute('template') || null,
                        scope: getScopeInfo(el),
                        parentChain,
                    };
                });

            const components = Array.from(
                document.querySelectorAll('a-component')
            )
                .slice(0, 80)
                .map((el, index) => ({
                    index,
                    template: el.getAttribute('template'),
                    xData: el.getAttribute('x-data')?.slice(0, 300) || null,
                    connected: el.isConnected,
                    parentXData:
                        el.parentElement?.closest('[x-data]')
                            ?.getAttribute('x-data')
                            ?.slice(0, 300) || null,
                }));

            addTags({ background_queue_scope_error: 'true' });

            addExtra({
                backgroundQueueDiagnostics: {
                    error: errorValue,
                    rootFound: Boolean(root),
                    rootConnected: root?.isConnected ?? false,
                    rootScope: root ? getScopeInfo(root) : null,
                    totalAlpineScopes: scopeNodes.length,
                    alpineScopes,
                    components,
                    stackFrames: frames.slice(-10).map(frame => ({
                        filename: frame.filename,
                        function: frame.function,
                        lineno: frame.lineno,
                        colno: frame.colno,
                        contextLine: frame.context_line,
                    })),
                    queueBreadcrumbs,
                },
                page: {
                    url: location.href,
                    pathname: location.pathname,
                },
            });
        }
    }

    // TypeError: items is not iterable
    {
        const itemsDiagnosticText = [
            errorValue,
            message,
            error?.message,
        ]
            .filter(value => typeof value === 'string')
            .join('\n');

        const isItemsNotIterable =
            /\bitems\b[\s\S]{0,100}\bnot iterable\b/i.test(itemsDiagnosticText) ||
            /\bnot iterable\b[\s\S]{0,100}\bitems\b/i.test(itemsDiagnosticText);

        if (isItemsNotIterable) {
            const alpine =
                typeof window !== 'undefined'
                    ? window.Alpine
                    : undefined;

            const safeReadAttribute = (element, name) => {
                try {
                    return element?.getAttribute?.(name) ?? null;
                } catch {
                    return null;
                }
            };

            const safeKeys = (value, limit = 40) => {
                try {
                    if (
                        value &&
                        (typeof value === 'object' || typeof value === 'function')
                    ) {
                        return Object.keys(value).slice(0, limit);
                    }
                } catch {
                    // A proxy or an object under mutation may not be inspectable.
                }

                return [];
            };

            const describeValue = value => {
                let type;
                let constructor = null;
                let iterable = false;
                let asyncIterable = false;

                if (value === null) {
                    type = 'null';
                } else {
                    type = typeof value;
                }

                try {
                    constructor = value?.constructor?.name || null;
                } catch {
                    constructor = '[unavailable]';
                }

                try {
                    iterable =
                        value != null &&
                        typeof value[Symbol.iterator] === 'function';
                } catch {
                    iterable = null;
                }

                try {
                    asyncIterable =
                        value != null &&
                        typeof value[Symbol.asyncIterator] === 'function';
                } catch {
                    asyncIterable = null;
                }

                if (Array.isArray(value)) {
                    let sample = [];

                    try {
                        sample = value.slice(0, 5).map(item => ({
                            type: item === null ? 'null' : typeof item,
                            keys: safeKeys(item, 20),
                        }));
                    } catch {
                        sample = '[unavailable]';
                    }

                    return {
                        type,
                        constructor,
                        isArray: true,
                        iterable,
                        asyncIterable,
                        length: value.length,
                        sample,
                    };
                }

                return {
                    type,
                    constructor,
                    isArray: false,
                    iterable,
                    asyncIterable,
                    keys: safeKeys(value),
                };
            };

            const getAlpineScopes = element => {
                const scopes = [];
                const seen = new Set();

                const addScope = scope => {
                    if (
                        scope &&
                        ['object', 'function'].includes(typeof scope) &&
                        !seen.has(scope)
                    ) {
                        seen.add(scope);
                        scopes.push(scope);
                    }
                };

                let current = element;
                let depth = 0;

                while (current && depth < 12) {
                    try {
                        if (Array.isArray(current._x_dataStack)) {
                            current._x_dataStack.forEach(addScope);
                        }
                    } catch {
                        // Alpine's internal scope may be unavailable.
                    }

                    try {
                        if (typeof alpine?.$data === 'function') {
                            addScope(alpine.$data(current));
                        }
                    } catch {
                        // Scope resolution may fail during a DOM update.
                    }

                    current = current.parentElement;
                    depth++;
                }

                return scopes;
            };

            const resolvePath = (scope, path) => {
                if (!scope || !path) {
                    return { found: false };
                }

                try {
                    let value = scope;

                    for (const key of path.split('.')) {
                        if (value == null || !(key in Object(value))) {
                            return { found: false };
                        }

                        value = value[key];
                    }

                    return {
                        found: true,
                        value,
                    };
                } catch {
                    return {
                        found: false,
                        inspectionFailed: true,
                    };
                }
            };

            const describeElement = element => {
                if (!element?.tagName) {
                    return null;
                }

                return {
                    tag: String(element.tagName).toLowerCase(),
                    id: safeReadAttribute(element, 'id'),
                    className: safeReadAttribute(element, 'class'),
                    xData: safeReadAttribute(element, 'x-data')?.slice(0, 500) ?? null,
                    xFor: safeReadAttribute(element, 'x-for')?.slice(0, 500) ?? null,
                    keyExpression:
                        safeReadAttribute(element, ':key') ||
                        safeReadAttribute(element, 'x-bind:key'),
                    connected: Boolean(element.isConnected),
                };
            };

            const errorElement =
                error?.el &&
                typeof error.el.getAttribute === 'function'
                    ? error.el
                    : error?.element &&
                    typeof error.element.getAttribute === 'function'
                        ? error.element
                        : null;

            let originalXFor = null;

            try {
                originalXFor = errorElement
                    ? errorElement.matches('[x-for]')
                        ? errorElement
                        : errorElement.closest('[x-for]')
                    : null;
            } catch {
                originalXFor = null;
            }

            const describeXFor = template => {
                const expression = safeReadAttribute(template, 'x-for');
                const keyExpression =
                    safeReadAttribute(template, ':key') ||
                    safeReadAttribute(template, 'x-bind:key');

                // Supports simple collections such as "item in items"
                // and "item in state.items".
                const pathMatch = expression?.match(
                    /\b(?:in|of)\s+([$\w]+(?:\.[\w$]+)*)/
                );

                const collectionPath = pathMatch?.[1] || null;
                const scopes = getAlpineScopes(
                    template?.parentElement || template
                );

                let collectionFound = false;
                let collection = null;
                let collectionScopeKeys = [];

                if (collectionPath) {
                    for (const scope of scopes) {
                        const resolved = resolvePath(scope, collectionPath);

                        if (resolved.found) {
                            collectionFound = true;
                            collection = describeValue(resolved.value);
                            collectionScopeKeys = safeKeys(scope, 80);
                            break;
                        }
                    }
                }

                const root = template?.closest?.('[x-data]');

                return {
                    element: describeElement(template),
                    parent: describeElement(template?.parentElement),
                    alpineRoot: describeElement(root),
                    collectionPath,
                    collectionFound,
                    collection,
                    collectionScopeKeys,
                    scopeCount: scopes.length,
                    scopeKeys: [
                        ...new Set(scopes.flatMap(scope => safeKeys(scope, 80))),
                    ].slice(0, 100),
                    hasPreviousKeys: Array.isArray(template?._x_prevKeys),
                    previousKeys: Array.isArray(template?._x_prevKeys)
                        ? template._x_prevKeys.slice(0, 20).map(value => {
                            try {
                                return ['string', 'number', 'boolean'].includes(typeof value)
                                    ? value
                                    : value == null
                                        ? value
                                        : `[${typeof value}]`;
                            } catch {
                                return '[unavailable]';
                            }
                        })
                        : null,
                    hasLookup: Boolean(template?._x_lookup),
                    connected: Boolean(template?.isConnected),
                };
            };

            const allXForTemplates =
                typeof document !== 'undefined'
                    ? Array.from(document.querySelectorAll('[x-for]'))
                    : [];

            const rankedXForTemplates = allXForTemplates
                .map((template, index) => {
                    const expression =
                        safeReadAttribute(template, 'x-for') || '';

                    let score = 0;

                    if (template === originalXFor) {
                        score += 1000;
                    }

                    if (/\bitems\b/i.test(expression)) {
                        score += 100;
                    }

                    if (
                        errorElement &&
                        (
                            template.contains(errorElement) ||
                            errorElement.contains(template)
                        )
                    ) {
                        score += 50;
                    }

                    return { template, index, score };
                })
                .sort((a, b) => b.score - a.score || a.index - b.index);

            const selectedXForTemplates = rankedXForTemplates
                .filter(item => item.score > 0)
                .slice(0, 20);

            // If no obvious match exists, include a small DOM sample.
            if (selectedXForTemplates.length === 0) {
                selectedXForTemplates.push(
                    ...rankedXForTemplates.slice(0, 5)
                );
            }

            const sanitizeUrl = value => {
                if (typeof value !== 'string' || !value) {
                    return null;
                }

                try {
                    const url = new URL(value, location.href);

                    // Intentionally omit query parameters and fragments.
                    return `${url.origin}${url.pathname}`.slice(0, 300);
                } catch {
                    return value.split(/[?#]/)[0].slice(0, 300);
                }
            };

            const recentBreadcrumbs = getBreadcrumbs()
                .slice(-40)
                .map(breadcrumb => {
                    const result = {
                        timestamp: breadcrumb?.timestamp,
                        category: breadcrumb?.category,
                        type: breadcrumb?.type,
                        level: breadcrumb?.level,
                        message: typeof breadcrumb?.message === 'string'
                            ? breadcrumb.message.slice(0, 500)
                            : null,
                    };

                    if (
                        breadcrumb?.category === 'fetch' ||
                        breadcrumb?.category === 'xhr'
                    ) {
                        result.method = breadcrumb?.data?.method || null;
                        result.statusCode =
                            breadcrumb?.data?.status_code ?? null;
                        result.url = sanitizeUrl(breadcrumb?.data?.url);
                    }

                    if (breadcrumb?.category === 'console') {
                        const args = Array.isArray(breadcrumb?.data?.arguments)
                            ? breadcrumb.data.arguments
                            : [];

                        result.relevantConsoleMessages = args
                            .filter(value =>
                                typeof value === 'string' &&
                                /items|not iterable|alpine|feedback|x-for/i.test(value)
                            )
                            .slice(-5)
                            .map(value => value.slice(0, 500));
                    }

                    return result;
                });

            const scriptInventory = Array.from(document.scripts)
                .slice(0, 80)
                .map((script, index) => ({
                    index,
                    src: script.src ? sanitizeUrl(script.src) : null,
                    id: script.id || null,
                    type: script.type || 'classic',
                    inlineLength: script.src
                        ? null
                        : (script.textContent || '').length,
                }));

            let jsResources = [];

            try {
                jsResources = performance
                    .getEntriesByType('resource')
                    .filter(resource =>
                        resource.initiatorType === 'script' ||
                        /\.m?js(?:[?#]|$)/i.test(resource.name)
                    )
                    .slice(-40)
                    .map(resource => ({
                        url: sanitizeUrl(resource.name),
                        initiatorType: resource.initiatorType,
                        duration: Math.round(resource.duration),
                        transferSize: resource.transferSize || null,
                    }));
            } catch {
                jsResources = [];
            }

            addTags({
                items_not_iterable_diagnostic: 'true',
                items_not_iterable_alpine_element: errorElement ? 'true' : 'false',
                items_not_iterable_x_for_match:
                    allXForTemplates.some(template =>
                        /\bitems\b/i.test(
                            safeReadAttribute(template, 'x-for') || ''
                        )
                    )
                        ? 'true'
                        : 'false',
            });

            addExtra({
                itemsNotIterableDiagnostics: {
                    error: {
                        type: exception?.type || null,
                        value: String(errorValue).slice(0, 1000),
                        originalMessage: error?.message || null,
                        originalStack: error?.stack || null,
                    },

                    page: getPageContext(),
                    browser: getBrowserContext(true),

                    targetElement: describeElement(errorElement),
                    originalXForFound: Boolean(originalXFor),
                    totalXForTemplates: allXForTemplates.length,

                    xForTemplates: selectedXForTemplates.map(
                        item => describeXFor(item.template)
                    ),

                    stackFrames: frames
                        .slice(-15)
                        .map(getFrameContext),

                    recentBreadcrumbs: recentBreadcrumbs.slice(-30),
                    scripts: scriptInventory,
                    jsResources,
                },
            });
        }
    }

    // Native fetch failures
    {
        const fetchErrorText = [
            errorValue,
            message,
            error?.message,
        ]
            .filter(value => typeof value === 'string')
            .join('\n');

        const isFetchFailure =
            Boolean(error?.fetchUrl) ||
            /\bFailed to fetch\b/i.test(fetchErrorText);

        if (isFetchFailure) {
            const rawUrl =
                typeof error?.fetchUrl === 'string'
                    ? error.fetchUrl
                    : null;

            const describeUrl = value => {
                if (!value) {
                    return null;
                }

                try {
                    const url = new URL(value, location.href);

                    return {
                        origin: url.origin,
                        pathname: url.pathname,
                        sameOrigin: url.origin === location.origin,
                        hasQuery: Boolean(url.search),
                    };
                } catch {
                    return {
                        available: false,
                        valueType: typeof value,
                    };
                }
            };

            const failedUrl = describeUrl(rawUrl);

            const recentNetwork = getBreadcrumbs()
                .filter(b =>
                    b?.category === 'fetch' ||
                    b?.category === 'xhr'
                )
                .slice(-20)
                .map(b => ({
                    timestamp: b.timestamp,
                    category: b.category,
                    method: b?.data?.method || null,
                    statusCode: b?.data?.status_code ?? null,
                    url: describeUrl(b?.data?.url),
                }));

            addTags({
                native_fetch_failure_diagnostic: 'true',
                native_fetch_url_available: rawUrl ? 'true' : 'false',
                native_fetch_origin:
                    failedUrl?.sameOrigin === true
                        ? 'same-origin'
                        : failedUrl?.sameOrigin === false
                            ? 'cross-origin'
                            : 'unknown',
            });

            addExtra({
                nativeFetchFailure: {
                    errorType: error?.name || exception?.type || null,
                    errorMessage: error?.message || errorValue || null,
                    fetchUrl: failedUrl,

                    page: getPageContext(),

                    browser: {
                        userAgent: navigator.userAgent,
                        online: navigator.onLine,
                        visibilityState: document.visibilityState,
                    },

                    recentNetwork,
                    lastBreadcrumbs: getLastBreadcrumbs(30),
                },
            });
        }
    }

    // Diagnose Alpine userWishlists / hasCustomLists errors.
    {
        const originalError = hint?.originalException;
        const exceptionValues = event?.exception?.values || [];

        const diagnosticText = [
            event?.message,
            originalError?.message,
            originalError?.expression,
            ...exceptionValues.map(item => item?.value),
        ]
            .filter(value => typeof value === 'string')
            .join('\n');

        if (/userWishlists|hasCustomLists/i.test(diagnosticText)) {
            const diagnostic = {
                page: window.location.pathname,
                readyState: document.readyState,
                visibilityState: document.visibilityState,
                online: navigator.onLine,

                alpineAvailable: Boolean(window.Alpine),

                modelStore: {
                    found: false,
                    hasOwnUserWishlists: null,
                    valueType: 'unavailable',
                    isArray: null,
                    length: null,
                    readError: null,
                },

                components: [],
            };

            try {
                const store = window.Alpine?.store?.('model');

                diagnostic.modelStore.found = store != null;

                if (store != null) {
                    diagnostic.modelStore.hasOwnUserWishlists =
                        Object.prototype.hasOwnProperty.call(
                            store,
                            'userWishlists'
                        );

                    const lists = store.userWishlists;

                    diagnostic.modelStore.valueType =
                        lists === null
                            ? 'null'
                            : Array.isArray(lists)
                                ? 'array'
                                : typeof lists;

                    diagnostic.modelStore.isArray = Array.isArray(lists);

                    if (Array.isArray(lists)) {
                        diagnostic.modelStore.length = lists.length;
                    }
                }
            } catch (err) {
                diagnostic.modelStore.readError = err?.name || 'Error';
            }

            try {
                diagnostic.components = Array.from(
                    document.querySelectorAll('a-component')
                )
                    .slice(0, 20)
                    .map(component => ({
                        template:
                            component.getAttribute('template')
                                ?.split('?')[0] || null,
                        connected: component.isConnected,
                        hasShadowRoot: Boolean(component.shadowRoot),
                    }));
            } catch {
                // Diagnostic failure must not affect Sentry capture.
            }

            event.extra = {
                ...(event.extra || {}),
                userWishlistsDiagnostic: diagnostic,
            };

            event.tags = {
                ...(event.tags || {}),
                user_wishlists_state: !diagnostic.modelStore.found
                    ? 'model-store-missing'
                    : diagnostic.modelStore.valueType,
            };
        }
    }

    // Model upload onInput null.id diagnostics
    if (location.pathname === '/stock/create' && /Cannot read properties of null \(reading ['"]id['"]\)/i.test(errorValue)) {
        const frame = [...frames].reverse().find(frame =>
            /(?:^|\.)onInput$/.test(frame?.function || '') &&
            Number.isInteger(frame?.lineno) &&
            Number.isInteger(frame?.colno)
        );

        if (frame) {
            const diagnostic = {
                function: frame.function,
                line: frame.lineno,
                column: frame.colno,
                status: 'not-checked',
                sourceVerified: false,
                expression: null,
                expressionCandidates: [],
            };

            let controller = null;
            let timeoutId = null;

            try {
                const frameUrl = new URL(frame.filename, location.href);

                if (
                    frameUrl.origin !== location.origin ||
                    frameUrl.pathname !== location.pathname
                ) {
                    diagnostic.status = 'different-source';
                } else {
                    controller = new AbortController();

                    timeoutId = setTimeout(() => {
                        controller.abort();
                    }, 3000);

                    const response = await fetch(location.href, {
                        credentials: 'same-origin',
                        cache: 'force-cache',
                        signal: controller.signal,
                    });

                    if (!response.ok) {
                        diagnostic.status = `http-${response.status}`;
                    } else {
                        const html = await response.text();
                        const lines = html.split('\n');
                        const sourceLine = lines[frame.lineno - 1];

                        if (sourceLine === undefined) {
                            diagnostic.status = 'line-not-found';
                        } else {
                            const lineOffset = lines
                                .slice(0, frame.lineno - 1)
                                .reduce((sum, line) => sum + line.length + 1, 0);

                            const sourceOffset = lineOffset + frame.colno - 1;

                            const scriptRegex = /<script\b[^>]*>([\s\S]*?)<\/script\s*>/gi;

                            let match;
                            let scriptBody = null;

                            while ((match = scriptRegex.exec(html)) !== null) {
                                const bodyStart = match.index + match[0].indexOf('>') + 1;
                                const bodyEnd = bodyStart + match[1].length;

                                if (
                                    sourceOffset >= bodyStart &&
                                    sourceOffset < bodyEnd
                                ) {
                                    scriptBody = match[1];
                                    break;
                                }
                            }

                            if (scriptBody === null) {
                                diagnostic.status = 'inline-script-not-found';
                            } else {
                                diagnostic.sourceVerified = Array.from(
                                    document.scripts
                                ).some(script =>
                                    !script.src &&
                                    script.textContent === scriptBody
                                );

                                if (!diagnostic.sourceVerified) {
                                    diagnostic.status = 'source-version-mismatch';
                                } else {
                                    const expressionRegex =
                                        /\b[A-Za-z_$][\w$]*(?:\??\.[A-Za-z_$][\w$]*)*\.id\b/g;

                                    const expressions = [];
                                    let expressionMatch;

                                    while (
                                        (expressionMatch = expressionRegex.exec(sourceLine)) !== null
                                        ) {
                                        expressions.push({
                                            expression: expressionMatch[0],
                                            column: expressionMatch.index + 1,
                                            endColumn: expressionMatch.index + expressionMatch[0].length,
                                        });
                                    }

                                    diagnostic.expressionCandidates = expressions;

                                    const column = frame.colno;

                                    const matchingExpression = expressions.find(item =>
                                        column >= item.column &&
                                        column <= item.endColumn + 1
                                    );

                                    diagnostic.expression =
                                        matchingExpression?.expression || null;

                                    diagnostic.status = matchingExpression
                                        ? 'expression-found'
                                        : 'expression-not-found';
                                }
                            }
                        }
                    }
                }
            } catch (err) {
                diagnostic.status = 'diagnostic-error';
                diagnostic.error = err?.name || 'Error';
            } finally {
                if (timeoutId !== null) {
                    clearTimeout(timeoutId);
                }
            }

            addExtra({
                modelUploadOnInputSource: diagnostic,
            });

            addTags({
                model_upload_oninput_source: diagnostic.status,
            });
        }
    }
};
